
import { mitWeiterenAdressen, type KanalOwner, type MailKanal } from '../../../lib/kontakte/weitereAdressen';
import { betreffMitZusatz } from '../../../lib/emails/betreffZusatz';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { raeumeUeberholteEntwuerfeAuf } from '../../../lib/emails/entwurfAufraeumen';
import { supabase } from '../../../lib/supabase/client';
import { htmlZuText, ersetzePlatzhalter, type PlatzhalterWerte } from '../../../lib/aktionen/platzhalter';
import { eigentuemerAnzeigename, istFirma, briefAnrede } from '../../../lib/eigentuemer/anzeige';
import { bereinigeSuchbegriff } from '../../../lib/kontakte/personenSuche';
import { sucheDateien, ladeDateiBlob, ladeOrdnerInhalt, formatiereGroesse, istGoogleFormat, ORDNER_MIME, type SuchTreffer, type DriveEintrag } from '../../../lib/dokumente/aktenschrankApi';
import { spaeterSendenPresets, teileAmZitat } from '../../../lib/emails/wiedervorlage';
import { baueZitatKarte, zerlegeEntwurfHtml } from '../../../lib/emails/zitat';
import { bereinigeZitatHtml } from '../../../lib/emails/emailHtml';
import { anzahlEmpfaenger, versandFehler } from '../../../lib/emails/versand';
import { antwortEmpfaenger } from '../../../lib/emails/antwortEmpfaenger';
import { adressenAusEingabe } from '../../../lib/emails/empfaenger';
import { darfPromptAendern, ladePrompt, speicherePrompt, STANDARD_REGELN } from '../../../lib/ki/promptEinstellung';
import { ladeAnhangBlob } from '../../../lib/emails/anhangDatei';
import { istKompakt } from '../../../components/island/island-size';
import { useViewport } from '../../../components/island/hooks';
import {
  Send, X, Loader2, Paperclip, FileText, FileSignature, Newspaper, Archive, Folder, ChevronRight, Search,
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link2, Palette, Highlighter, Type, RemoveFormatting, Clock, Sparkles, Plus, Pencil,
} from 'lucide-react';
import { speicherName } from '../../../lib/storage/speicherName';
import { createPortal } from 'react-dom';
import {
  ArrowsOutSimple, Columns, Minus, PencilSimpleLine, X as XPh,
  Archive as PhArchive, CaretRight as PhCaretRight, Check as PhCheck, Clock as PhClock, FileText as PhFileText, Folder as PhFolder,
  MagnifyingGlass as PhLupe, Newspaper as PhNewspaper, PaperPlaneTilt, Paperclip as PhPaperclip, PencilSimple as PhPencil,
  Envelope as PhEnvelope, Plus as PhPlus, Signature as PhSignature, Sparkle as PhSparkle, TextAlignLeft as PhTextAlignLeft, Users as PhUsers,
} from '@phosphor-icons/react';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormFeld } from '../../../components/island/form/FormFeld';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { FormDock } from '../../../components/island/form/FormDock';
import { FormPopover, FormPopoverZeile } from '../../../components/island/form/FormPopover';
import { DOCK_VERLAUF, FELD, FORM, KNOPF, POPOVER, ZEILEN_ICON } from '../../../components/island/form/form-stil';
import EmailHtmlAnsicht from './EmailHtmlAnsicht';
import { FENSTER_RAND, useInhaltsflaecheLinks } from '../../../components/immobilien/ui/GlassModal';
import { ohneArchivierte } from '../../../lib/team/archiv';
import { ohneDoppeltenGruss } from '../../../supabase/functions/_shared/grussformel';
import { PostVersandDialog, type PostAnlage } from '../dokumente/post/PostVersandDialog';
import { findeEmpfaengerZuEmail, ladeMailAnhang, mimeAusName } from '../../../lib/epost/emailZuBrief';
import { htmlZuText as briefTextAusHtml } from '../../../lib/dokumente/briefDaten';
import type { EmpfaengerTreffer } from '../../../lib/dokumente/briefApi';

// Farbige Avatar-Initialen für Empfänger-Chips.
const AVATAR_FARBEN = ['bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-teal-500', 'bg-sky-500', 'bg-indigo-500', 'bg-violet-500', 'bg-fuchsia-500'];
const avatarFarbe = (s: string): string => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_FARBEN[h % AVATAR_FARBEN.length];
};
const initialen = (s: string): string => {
  const parts = (s || '?').trim().split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface EmailVerfassenProps {
  konto: { id: string; email_adresse: string; bezeichnung: string };
  replyTo?: {
    id: string;
    von: string;
    betreff: string;
    html_inhalt?: string;
    text_inhalt?: string;
    message_id?: string;
    an: string[];
    cc?: string[];
    /** „Allen antworten": übrige Empfänger der Ursprungsmail ins Cc vorbelegen. */
    allen?: boolean;
    datum?: string;
  };
  forward?: {
    /** Ursprungsmail — deren Anhänge werden beim Öffnen übernommen (sonst gingen sie verloren). */
    id?: string;
    betreff: string;
    html_inhalt?: string;
    text_inhalt?: string;
    datum?: string;
  };
  draft?: {
    id?: string;
    an: string[];
    cc?: string[];
    bcc?: string[];
    betreff: string;
    text_inhalt: string;
    html_inhalt?: string;
    anhaenge?: { id?: string; name: string; path: string; size: number }[];
  };
  onClose: () => void;
  /**
   * Nach erfolgreichem Versand. `info` liefert den final aufgelösten Betreff + Klartext
   * sowie die mitgesendeten Anhänge — z. B. um die Nachricht am Ticket als internen
   * Kommentar zu spiegeln. Bestehende Aufrufer ohne Parameter bleiben unverändert gültig.
   */
  onSent: (info?: {
    betreff: string; text: string; anhaenge: { name: string; size: number; path: string }[]; emailId?: string | null;
    /** Tatsächlich angeschriebene Adressen — Ticket-Flows protokollieren sie im Verlauf. */
    an?: string[]; cc?: string[]; bcc?: string[];
  }) => void;
  /**
   * 'inline' rendert das Formular als Spalte (im Detail-Panel) statt als zentriertes Modal.
   * 'pille' rendert die dunkle Variante für den Pillen-Formularbereich (ohne eigenen Header —
   * die Pille liefert Titelzeile + Schließen).
   * 'fenster' ist das große Schreibfenster des E-Mail-Bereichs (Handoff E-Mails §6, ComposeWindow):
   * 960 px, „Original“-Spalte, Minimieren zur Leiste, „Entwurf gespeichert · HH:MM“, Token-Farben.
   */
  variant?: 'modal' | 'inline' | 'pille' | 'fenster';
  /** Titel im Fenster statt „Antworten/Weiterleiten/Neue E-Mail“ (z. B. „Entwurf“). */
  titel?: string;
  /**
   * Wendet automatisch die Vorlage mit diesem ki_trigger an, sobald Empfänger + Briefpapier
   * bereit sind — über denselben Pfad wie die manuelle Vorlagenauswahl (korrekt im Briefbogen,
   * kein Rohtext im Plain-Editor). Für vorausgefüllte Aktionen wie „Rechnung an Eigentümer".
   */
  autoVorlageTrigger?: string;
  /**
   * #525: wird an den Betreff einer angewendeten Vorlage gehängt, wenn er noch nicht darin steht
   * (z. B. Objekt bei Angebot/Beauftragung – die Vorlagen-Betreffs sind allgemein gehalten).
   */
  betreffZusatz?: string;
  /**
   * Zusätzliche Platzhalter-Werte für die Auto-Vorlage (überlagern die empfänger-basierte
   * Auflösung). Nötig, wenn der Empfänger die Platzhalter nicht hergibt — z. B. Handwerker-
   * Beauftragung: {{objekt.*}}/Meldender kommen aus dem Ticket, nicht aus der Dienstleister-
   * E-Mail. Mit gesetzten Werten wird die Vorlage auch ohne Empfänger angewendet.
   */
  autoVorlageWerte?: Record<string, string>;
  /**
   * Hängt beim Öffnen eine Google-Drive-Datei als Anhang an (im Hintergrund geladen, mit
   * Ladeanzeige) — so öffnet das Formular sofort statt erst nach dem PDF-Download.
   */
  autoAnhang?: { fileId: string; name: string; mimeType?: string };
  /**
   * Mehrere Drive-Anhänge auf einmal (Sammelversand von Belegen). Wird zusammen mit
   * autoAnhang unterstützt — beide Listen landen im selben Anhang-Bereich.
   */
  autoAnhaenge?: { fileId: string; name: string; mimeType?: string }[];
  /**
   * Anhänge, die bereits in einem Supabase-Storage-Bucket liegen (z. B. das fertig
   * unterschriebene PDF aus dem Bucket „signaturen"). Werden wie die Drive-Anhänge im
   * Hintergrund geladen und in den Anhang-Bucket kopiert.
   */
  autoAnhaengeStorage?: { bucket: string; path: string; name: string; mimeType?: string }[];
  /**
   * Startordner für den Aktenschrank-Picker (Google Drive) — z. B. der Drive-Ordner des
   * Ticket-Objekts. Breadcrumb behält „Aktenschrank" als Wurzel, die Suche bleibt global.
   */
  aktenStartOrdner?: { id: string; name: string };
  /**
   * Vorbereiteter Freitext-Inhalt (z. B. Auftragstext „Handwerker beauftragen") — wird ERST
   * nach dem Laden von Signaturen + Briefpapier angewendet, über denselben Pfad wie eine
   * Vorlage (bei aktivem Briefpapier in die Textzone im Bogen). Für vorbefüllte Aktionen
   * statt draft.text_inhalt verwenden: der Draft-Weg rendert den Text sonst doppelt
   * (roh + im Bogen), siehe ET-Weiterleitungs-Fix.
   */
  autoText?: string;
}

interface Empfaenger {
  email: string;
  name: string;
  typ: 'Mieter' | 'Eigentümer' | 'Dienstleister' | 'Kontakt' | 'Team';
}

interface SucheErgebnis extends Empfaenger {
  id: string;
  /** #569: Bezeichnung einer weiteren Adresse (z. B. „Buchhaltung“) */
  label?: string | null;
}

interface Vorlage {
  id: string;
  name: string;
  betreff_template: string;
  html_template: string | null;
  ki_trigger?: string | null;
}

interface Anhang {
  id: string;
  name: string;
  size: number;
  path: string;
  isDraft?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// supabase-js wirft bei jedem Function-Fehler nur „Edge Function returned a non-2xx
// status code" — der Grund steckt im Response-Body und ging bisher verloren. Genau
// deshalb stand der Nutzer vor einer Fehlermeldung ohne Ursache (2026-08-13).
async function funktionsFehlerText(fehler: unknown): Promise<string> {
  const antwort = (fehler as { context?: Response })?.context;
  if (antwort && typeof antwort.text === 'function') {
    try {
      const roh = await antwort.text();
      const echt = (JSON.parse(roh) as { error?: string })?.error || roh;
      if (echt?.trim()) return echt.trim();
    } catch {
      // Body nicht lesbar/kein JSON — unten der Standardtext
    }
  }
  return (fehler as Error)?.message || 'Unbekannter Fehler beim Versand.';
}

// datetime-local liefert lokale Zeit ohne Zone — als lokale Date interpretieren.
// (Bewusstes 3-Zeilen-Duplikat aus WiedervorlageWahl in bereich/liste/EmailKontextmenue.tsx — für so wenig Code kein eigenes Modul.)
function lokalZuIso(v: string): string | null {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// ─── Component ───────────────────────────────────────────────────────────────

// Plaintext-Editorinhalt -> HTML: Sonderzeichen escapen, Zeilenumbrüche als <br>,
// damit Absätze/Umbrüche in der gesendeten (HTML-)Mail erhalten bleiben.
function textZuHtml(t: string): string {
  return (t || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r\n|\r|\n/g, '<br>');
}

const EmailVerfassen: React.FC<EmailVerfassenProps> = ({
  konto, replyTo, forward, draft, onClose, onSent, variant = 'modal', titel, autoVorlageTrigger, betreffZusatz, autoVorlageWerte, autoAnhang, autoAnhaenge, autoAnhaengeStorage, aktenStartOrdner, autoText
}) => {
  // Ganz oben definiert (keine Abhängigkeiten außer DOMPurify) — der zitatHtml-State-Initializer
  // weiter unten braucht sie bereits beim ALLERERSTEN Render, um einen aus einem Entwurf
  // geladenen Zitat-Block VOR dem Einsetzen ins DOM zu säubern (Stored-XSS-Schutz beim
  // Entwurf-Reload, siehe zitatHtml-Init).
  const sanitizeHtml = (html: string): string =>
    DOMPurify.sanitize(html || '', { USE_PROFILES: { html: true }, FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'] });

  const viewport = useViewport();

  // Zitat-Inhalt der Ursprungsmail: bereinigtes HTML (Formatierung, Listen, Tabellen, Links bleiben;
  // Freigabe 15.10.2026). Bleibt nach dem Bereinigen kein Text übrig (z. B. reine Bild-Mail), wie
  // bisher die Textfassung. Leer -> null (keine Karte).
  const zitatInhalt = (html?: string, text?: string): string | null => {
    if (html && html.trim()) {
      const sauber = bereinigeZitatHtml(html);
      if (htmlZuText(sauber).trim()) return sauber;
    }
    const roh = text || (html ? htmlZuText(html) : '');
    return roh.trim() ? textZuHtml(roh) : null;
  };

  // Mode
  const mode = replyTo ? 'antwort' : forward ? 'weiterleiten' : 'neu';
  const modalTitleStandard = mode === 'antwort'
    ? (replyTo?.allen ? 'Allen antworten' : 'Antworten')
    : mode === 'weiterleiten' ? 'Weiterleiten' : 'Neue E-Mail';
  const modalTitle = titel ?? modalTitleStandard;

  // Empfänger einer Antwort kommen aus EINER Regel (lib/emails/antwortEmpfaenger):
  // An = Absender, bei „Allen antworten" zusätzlich die übrigen Empfänger im Cc — ohne das
  // eigene Postfach und ohne Dubletten.
  const antwortListen = replyTo
    ? antwortEmpfaenger({ von: replyTo.von, an: replyTo.an, cc: replyTo.cc }, konto.email_adresse, !!replyTo.allen)
    : null;
  const alsEmpfaenger = (e: string): Empfaenger => ({ email: e, name: e, typ: 'Team' });

  // Recipients
  const [an, setAn] = useState<Empfaenger[]>(() => {
    if (antwortListen) return antwortListen.an.map(alsEmpfaenger);
    if (draft) return draft.an.map(alsEmpfaenger);
    return [];
  });
  const [cc, setCc] = useState<Empfaenger[]>(() =>
    (antwortListen ? antwortListen.cc : draft?.cc || []).map(alsEmpfaenger));
  const [bcc, setBcc] = useState<Empfaenger[]>(() => draft?.bcc?.map(alsEmpfaenger) || []);
  const [showCcBcc, setShowCcBcc] = useState<boolean>(() =>
    !!(antwortListen?.cc.length || draft?.cc?.length || draft?.bcc?.length));

  // Search states per field
  const [anSearch, setAnSearch] = useState('');
  const [ccSearch, setCcSearch] = useState('');
  const [bccSearch, setBccSearch] = useState('');
  const [searchResults, setSearchResults] = useState<SucheErgebnis[]>([]);
  const [activeField, setActiveField] = useState<'an' | 'cc' | 'bcc' | null>(null);

  // Subject & body
  const [betreff, setBetreff] = useState(() => {
    if (replyTo) return `Re: ${replyTo.betreff.replace(/^Re:\s*/i, '')}`;
    if (forward) return `Fwd: ${forward.betreff.replace(/^Fwd:\s*/i, '')}`;
    if (draft) return draft.betreff;
    return '';
  });

  const [inhalt, setInhalt] = useState(() => {
    if (replyTo) return '';
    if (forward) return '';
    if (draft) return draft.text_inhalt;
    return '';
  });

  // Entwurf-HTML EINMAL zerlegen (eigener Text vs. Zitat-Karte, siehe zerlegeEntwurfHtml) —
  // der zitatHtml-Initializer (hier) UND der Editor-Mount-Effekt weiter unten lesen daraus,
  // damit beide exakt denselben Split sehen (sonst driften Editor-Startzustand und die beim
  // Unmount autogespeicherte Mail auseinander). Gecacht in einem Ref, weil ein State-Initializer
  // keine Hooks aufrufen darf — die Berechnung selbst ist eine reine Funktion, daher unschädlich,
  // mehrfach denselben (gecachten) Wert zu lesen.
  const entwurfZerlegungRef = useRef<{ eigenerHtml: string; zitatKarte: string | null } | null>(null);
  const entwurfZerlegung = (): { eigenerHtml: string; zitatKarte: string | null } => {
    if (!entwurfZerlegungRef.current) {
      entwurfZerlegungRef.current = draft?.html_inhalt
        ? zerlegeEntwurfHtml(draft.html_inhalt, { htmlZuText, textZuHtml, teileAmZitat })
        : { eigenerHtml: '', zitatKarte: null };
    }
    return entwurfZerlegungRef.current;
  };

  // Zitat der Ursprungs-/weitergeleiteten Mail — GETRENNT vom eigenen Text geführt
  // (Signatur rendert dazwischen); null = kein Zitat. Kein Setter — Bearbeitungen laufen über
  // zitatCacheRef/zitatEditorRef (contentEditable-Zone), siehe aktuellerZitatHtml().
  const [zitatHtml] = useState<string | null>(() => {
    if (replyTo) {
      // Leeres Original -> KEINE Kopfzeilen-only-Karte anbieten: aktuellerZitatHtml() verwirft sie
      // beim Senden ohnehin als leer — die Editor-Vorschau soll der gesendeten Mail entsprechen.
      const inhalt = zitatInhalt(replyTo.html_inhalt, replyTo.text_inhalt);
      return inhalt ? baueZitatKarte('antwort', replyTo.von, replyTo.datum ?? null, inhalt) : null;
    }
    if (forward) {
      const inhalt = zitatInhalt(forward.html_inhalt, forward.text_inhalt);
      return inhalt ? baueZitatKarte('weiterleitung', null, forward.datum ?? null, inhalt) : null;
    }
    if (draft?.html_inhalt) {
      const { zitatKarte } = entwurfZerlegung();
      // Aus dem Entwurf geladenes Zitat-HTML ist NICHT vertrauenswürdig (der Nutzer kann in die
      // editierbare Zitat-Zone beliebiges HTML pasten, das dann roh autogespeichert wird) — vor
      // dem Einsetzen ins DOM (zitatEditorRef.innerHTML / iframe-doc.write) hier an der Quelle
      // säubern, sonst Stored-XSS beim nächsten Öffnen dieses Entwurfs.
      return zitatKarte ? sanitizeHtml(zitatKarte) : null;
    }
    return null;
  });

  // Templates
  const [vorlagen, setVorlagen] = useState<Vorlage[]>([]);
  const [selectedVorlage, setSelectedVorlage] = useState('');
  // „Geladen"-Flags für Vorlage/Signatur/Briefpapier — nötig, um eine automatische
  // Vorlage (autoVorlageTrigger) erst anzuwenden, wenn ALLES stabil ist (sonst überschreiben
  // sich Signatur-/Briefpapier-Renders und der Vorlagentext gegenseitig).
  const [vorlagenGeladen, setVorlagenGeladen] = useState(false);
  const [signaturenGeladen, setSignaturenGeladen] = useState(false);
  const [briefpapierGeladen, setBriefpapierGeladen] = useState(false);

  // Attachments
  const [anhaenge, setAnhaenge] = useState<Anhang[]>(() =>
    draft?.anhaenge?.map((a, i) => ({ id: a.id || `draft-${i}`, name: a.name, size: a.size, path: a.path, isDraft: true })) || []
  );
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Wie viele Drive-Anhänge noch laden (Einzel-Anhang + Sammelliste) — die Zahl steht in der
  // Anhang-Überschrift, damit man beim Sammelversand sieht, dass noch etwas kommt.
  const [autoAnhangOffen, setAutoAnhangOffen] = useState(
    (autoAnhang ? 1 : 0) + (autoAnhaenge?.length || 0) + (autoAnhaengeStorage?.length || 0),
  );
  const autoAnhangLaedt = autoAnhangOffen > 0;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Aus dem Aktenschrank (Google Drive) anhängen — Ordner-Browser + Suche
  const [showAkten, setShowAkten] = useState(false);
  const [aktenSuche, setAktenSuche] = useState('');
  const [aktenTreffer, setAktenTreffer] = useState<SuchTreffer[]>([]);
  const [aktenLaedt, setAktenLaedt] = useState(false);
  const [aktenHolt, setAktenHolt] = useState<string | null>(null);
  const aktenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [aktenStack, setAktenStack] = useState<{ id: string; name: string }[]>(
    aktenStartOrdner ? [{ id: '', name: 'Aktenschrank' }, aktenStartOrdner] : [{ id: '', name: 'Aktenschrank' }],
  );
  const [aktenInhalt, setAktenInhalt] = useState<DriveEintrag[]>([]);
  const [aktenBrowseLaedt, setAktenBrowseLaedt] = useState(false);
  const aktenInitRef = useRef(false);

  // Sending
  const [sending, setSending] = useState(false);
  // E-POST: diese Mail als Brief (Text = Anschreiben, Anhänge im selben Umschlag)
  const [perPost, setPerPost] = useState<null | { betreff: string; text: string; anlagen: PostAnlage[]; treffer: EmpfaengerTreffer | null }>(null);
  const [postMeldung, setPostMeldung] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Später senden: Uhr-Dropdown mit Presets + freier Datum/Uhrzeit-Wahl (nur Zukunft).
  const [showSpaeter, setShowSpaeter] = useState(false);
  const [spaeterFrei, setSpaeterFrei] = useState('');

  // Entwurf / Auto-Speichern
  const draftIdRef = useRef<string | null>(draft?.id ?? null);
  const sentRef = useRef(false);                       // nach Versand: kein Entwurf mehr speichern
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speicherLaeuftRef = useRef(false);             // verhindert doppeltes Insert
  const userIdRef = useRef<string | null>(null);
  const ersterAutosaveRef = useRef(true);              // erste (Initial-)Renderung nicht speichern
  const [bodyDirty, setBodyDirty] = useState(0);       // Zähler: Editor-Text wurde geändert

  // Rich-Text-Editor (contentEditable) + schwebende Format-Toolbar + Vorlagen-Menü
  const editorRef = useRef<HTMLDivElement>(null);
  // Zitat-Zone im einfachen Editor — GETRENNT vom Haupt-Editor, siehe zitatHtml/zitatCacheRef.
  const zitatEditorRef = useRef<HTMLDivElement>(null);
  const [toolbar, setToolbar] = useState<{ top: number; left: number } | null>(null);
  const [showVorlagen, setShowVorlagen] = useState(false);

  // Signaturen — pro Mail wählbar; erscheint bei Briefpapier unten im Bogen, sonst unten angehängt.
  const [signaturen, setSignaturen] = useState<{ id: string; name: string; html_signatur: string; ist_standard: boolean }[]>([]);
  const [signaturId, setSignaturId] = useState<string>('none');
  const [showSignaturen, setShowSignaturen] = useState(false);

  // Briefpapier (firmenweiter HTML-Bogen mit {{inhalt}}/{{signatur}}); bei Auswahl WYSIWYG-Editor:
  // im Bogen schreiben, Signatur direkt darunter sichtbar.
  const [briefpapier, setBriefpapier] = useState<{ id: string; name: string; html: string; ist_standard: boolean }[]>([]);
  const [briefpapierId, setBriefpapierId] = useState<string>('none');
  const [showBriefpapier, setShowBriefpapier] = useState(false);
  const briefEditorRef = useRef<HTMLIFrameElement>(null);
  const inhaltCacheRef = useRef<string>('');          // aktueller Nutzertext (überlebt Editor-Wechsel)
  const zitatCacheRef = useRef<string>(zitatHtml ?? ''); // aktuelles Zitat-HTML (überlebt Editor-Wechsel)
  const prevBpRef = useRef<string>('none');           // vorheriges Briefpapier (für Inhalts-Übertrag)
  const [briefFormat, setBriefFormat] = useState<Record<string, boolean>>({}); // aktive Formate (fett/kursiv/…)
  const [showBriefFarbe, setShowBriefFarbe] = useState(false);
  const [showBriefMarkierung, setShowBriefMarkierung] = useState(false);
  const [showBriefGroesse, setShowBriefGroesse] = useState(false);

  // KI im Composer: eigenen Text überarbeiten (Plaintext-Runde) oder Antwort nach Anweisung
  // entwerfen; Vorschau vor dem Übernehmen. Arbeitet NUR auf dem eigenen Text (Body ohne
  // Zitat) — die getrennt geführte Zitat-Zone bleibt beim Übernehmen unangetastet.
  const [showKi, setShowKi] = useState(false);
  const [kiAnweisung, setKiAnweisung] = useState('');
  const [kiLaedt, setKiLaedt] = useState(false);
  const [kiFehler, setKiFehler] = useState<string | null>(null);
  const [kiVorschau, setKiVorschau] = useState<string | null>(null);
  // Regelwerk der Überarbeitung (Ticket #418): Admins können es hier ändern, alle anderen
  // sehen den Stift nicht. Das letzte Wort hat die RLS-Policy auf app_settings.
  const [kiDarfRegeln, setKiDarfRegeln] = useState(false);
  const [kiRegelnOffen, setKiRegelnOffen] = useState(false);
  const [kiRegeln, setKiRegeln] = useState('');
  const [kiRegelnSpeichert, setKiRegelnSpeichert] = useState(false);

  // Debounce ref
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Load Templates ──────────────────────────────────────────────────────

  useEffect(() => {
    const loadVorlagen = async () => {
      const { data } = await supabase
        .from('email_vorlagen')
        .select('id, name, betreff_template, html_template, ki_trigger')
        .order('name');
      if (data) setVorlagen(data);
      setVorlagenGeladen(true);
    };
    loadVorlagen();
  }, []);

  // ─── Load Signaturen ─────────────────────────────────────────────────────

  useEffect(() => {
    const loadSignaturen = async () => {
      const { data } = await supabase
        .from('email_signaturen')
        .select('id, name, html_signatur, ist_standard')
        .order('ist_standard', { ascending: false })
        .order('name');
      if (data) {
        setSignaturen(data);
        // Standard-Signatur (bzw. die erste) in ALLEN Modi vorauswählen — auch beim
        // Antworten/Weiterleiten (User-Wunsch 2026-07-27); erscheint unten im Briefbogen.
        const std = data.find((s) => s.ist_standard);
        setSignaturId(std ? std.id : (data[0]?.id ?? 'none'));
      }
      setSignaturenGeladen(true);
    };
    loadSignaturen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load Briefpapier ────────────────────────────────────────────────────

  useEffect(() => {
    const loadBriefpapier = async () => {
      const { data } = await supabase
        .from('email_briefpapier')
        .select('id, name, html, ist_standard')
        .order('ist_standard', { ascending: false })
        .order('name');
      if (data) {
        setBriefpapier(data);
        // Standard-Briefpapier (bzw. das einzige) in ALLEN Modi aktivieren — auch beim
        // Antworten/Weiterleiten (User-Wunsch 2026-07-27); das Zitat wandert mit in den Bogen.
        const std = data.find((b) => b.ist_standard) || (data.length === 1 ? data[0] : null);
        if (std) setBriefpapierId(std.id);
      }
      setBriefpapierGeladen(true);
    };
    loadBriefpapier();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Auto-Anhang aus Google Drive (Hintergrund, damit die Pille sofort öffnet) ──

  useEffect(() => {
    // Beide Quellen laufen durch DIESELBE Schleife: erst der Blob (Drive oder Storage),
    // dann Upload in den Anhang-Bucket — so bleibt der Zähler autoAnhangOffen die eine
    // Wahrheit für die Ladeanzeige.
    const liste: { name: string; hole: () => Promise<Blob> }[] = [
      ...[...(autoAnhang ? [autoAnhang] : []), ...(autoAnhaenge || [])].map((e) => ({
        name: e.name,
        hole: () => ladeDateiBlob(e.fileId, e.mimeType || 'application/pdf'),
      })),
      ...(autoAnhaengeStorage || []).map((e) => ({
        name: e.name,
        hole: async () => {
          const { data, error } = await supabase.storage.from(e.bucket).download(e.path);
          if (error || !data) throw new Error(error?.message || 'Datei nicht gefunden.');
          return data;
        },
      })),
    ];
    if (liste.length === 0) return;
    let abgebrochen = false;
    (async () => {
      // Nacheinander laden: 20 gleichzeitige Drive-Abrufe würden die Function ausbremsen,
      // und jeder fertige Anhang soll sofort in der Liste erscheinen.
      const fehlgeschlagen: string[] = [];
      for (const eintrag of liste) {
        try {
          const blob = await eintrag.hole();
          const path = `${konto.id}/${Date.now()}_${speicherName(eintrag.name)}`;
          const { error: upErr } = await supabase.storage.from('email-anhaenge')
            .upload(path, blob, { contentType: blob.type || 'application/pdf' });
          if (upErr) throw upErr;
          if (abgebrochen) return;
          setAnhaenge((prev) => [...prev, { id: crypto.randomUUID(), name: eintrag.name, size: blob.size, path }]);
        } catch (e: any) {
          fehlgeschlagen.push(eintrag.name);
          console.warn('Anhang konnte nicht geladen werden', eintrag.name, e?.message);
        } finally {
          if (!abgebrochen) setAutoAnhangOffen((n) => Math.max(0, n - 1));
        }
      }
      // Erst am Ende melden — sonst überschreibt ein später Fehler die Meldung des ersten.
      if (!abgebrochen && fehlgeschlagen.length > 0) {
        setError(`${fehlgeschlagen.length} Anhang/Anhänge konnten nicht geladen werden: ${fehlgeschlagen.join(', ')}`);
      }
    })();
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Anhänge der weitergeleiteten Mail übernehmen ────────────────────────
  // Weiterleiten ohne Anhänge ist keine Weiterleitung. Die Bytes liegen je nach Herkunft
  // im Bucket oder noch im Postfach — beides löst die Function „anhang-datei" auf; von dort
  // wandern sie wie ein normaler Upload in den Anhang-Bucket.

  useEffect(() => {
    const quelleId = forward?.id;
    if (!quelleId) return;
    let abgebrochen = false;
    (async () => {
      const { data, error: ladeFehler } = await supabase
        .from('email_anhaenge')
        .select('id, dateiname, dateityp, dateigroesse')
        .eq('email_id', quelleId)
        .order('created_at');
      if (ladeFehler) { console.warn('Anhänge der Ursprungsmail nicht lesbar', ladeFehler.message); return; }
      if (!data?.length || abgebrochen) return;
      setAutoAnhangOffen((n) => n + data.length);
      const fehlgeschlagen: string[] = [];
      for (const [i, a] of data.entries()) {
        try {
          const blob = await ladeAnhangBlob(a.id, a.dateityp);
          const path = `${konto.id}/${Date.now()}_${i}_${speicherName(a.dateiname, 'anhang')}`;
          const { error: upErr } = await supabase.storage.from('email-anhaenge')
            // Der Versand übernimmt den Content-Type des Bucket-Objekts, ladeAnhangBlob
            // vereinheitlicht ihn aber zum Schutz der Vorschau. Deshalb hier den echten
            // Typ der Ursprungsmail setzen — sonst käme ein .docx als octet-stream an.
            .upload(path, blob, { contentType: a.dateityp || blob.type || 'application/octet-stream' });
          if (upErr) throw upErr;
          if (abgebrochen) return;
          setAnhaenge((prev) => [...prev, {
            id: crypto.randomUUID(),
            name: a.dateiname || 'Anhang',
            size: a.dateigroesse || blob.size,
            path,
          }]);
        } catch (e: any) {
          fehlgeschlagen.push(a.dateiname || 'Anhang');
          console.warn('Anhang der Ursprungsmail konnte nicht übernommen werden', a.dateiname, e?.message);
        } finally {
          if (!abgebrochen) setAutoAnhangOffen((n) => Math.max(0, n - 1));
        }
      }
      if (!abgebrochen && fehlgeschlagen.length > 0) {
        setError(`${fehlgeschlagen.length} Anhang/Anhänge der Ursprungsmail konnten nicht übernommen werden: ${fehlgeschlagen.join(', ')}`);
      }
    })();
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Autocomplete Search ─────────────────────────────────────────────────

  const searchEmpfaenger = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    // Begriff bereinigen: er landet in PostgREST-`or`-Filtern, in denen Komma und
    // Klammern die Filterliste zerlegen wuerden (siehe lib/kontakte/personenSuche).
    const q = bereinigeSuchbegriff(query);
    if (q.length < 2) { setSearchResults([]); return; }
    const like = `%${q}%`;
    // Alle Quellen parallel abfragen (Muster: lib/kontakte/personenSuche) — fuenf
    // sequenzielle Roundtrips pro Tastendruck waren spuerbar traege.
    const [mieterRes, eigentuemerRes, dienstleisterRes, kontakteRes, profilesRes] = await Promise.all([
      supabase.from('mieter')
        .select('id, vorname, nachname, email')
        .or(`email.ilike.${like},vorname.ilike.${like},nachname.ilike.${like}`)
        .limit(5),
      // Eigentuemer inkl. Firmenname, damit Firmen auffindbar sind
      supabase.from('eigentuemer')
        .select('id, anrede, vorname, nachname, firma_name, email')
        .or(`email.ilike.${like},vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`)
        .limit(5),
      // Dienstleister liegen in einer EIGENEN Tabelle (Handwerker, Hausmeister,
      // Versicherungen …) — sie fehlten hier komplett und waren als Empfaenger
      // nicht auffindbar (Meldung Bjoern Schwede 2026-08-05).
      supabase.from('dienstleister')
        .select('id, name, ansprechpartner, email')
        .or(`email.ilike.${like},name.ilike.${like},ansprechpartner.ilike.${like}`)
        .limit(5),
      // Sonstige Kontakte (Behoerden, Verwaltungen …)
      supabase.from('kontakte')
        .select('id, vorname, nachname, firma_name, email')
        .or(`email.ilike.${like},vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`)
        .limit(5),
      supabase.from('profiles')
        .select('id, full_name, email, status')
        .or(`email.ilike.${like},full_name.ilike.${like}`)
        .limit(5),
    ]);

    const results: SucheErgebnis[] = [];

    (mieterRes.data || []).forEach((m: any) => {
      if (m.email) results.push({ id: m.id, email: m.email, name: `${m.vorname || ''} ${m.nachname || ''}`.trim(), typ: 'Mieter' });
    });
    (eigentuemerRes.data || []).forEach((e: any) => {
      if (e.email) results.push({ id: e.id, email: e.email, name: eigentuemerAnzeigename(e), typ: 'Eigentümer' });
    });
    (dienstleisterRes.data || []).forEach((d: any) => {
      if (d.email) results.push({ id: d.id, email: d.email, name: d.name || d.ansprechpartner || '', typ: 'Dienstleister' });
    });
    (kontakteRes.data || []).forEach((k: any) => {
      if (k.email) results.push({ id: k.id, email: k.email, name: k.firma_name || `${k.vorname || ''} ${k.nachname || ''}`.trim(), typ: 'Kontakt' });
    });
    ohneArchivierte(profilesRes.data || []).forEach((p: any) => {
      if (p.email) results.push({ id: p.id, email: p.email, name: p.full_name || '', typ: 'Team' });
    });

    // #569: weitere Mailadressen (kontakt_kanaele) — der gefundenen Kontakte und Kontakte, die nur über
    // eine Zweitadresse passen. So lässt sich z. B. die Buchhaltungsadresse direkt wählen.
    const ownerIds = [...(mieterRes.data || []), ...(eigentuemerRes.data || []), ...(dienstleisterRes.data || [])].map((x: any) => x.id as string);
    const [eigeneRes, treffendRes] = await Promise.all([
      ownerIds.length
        ? supabase.from('kontakt_kanaele').select('owner_type, owner_id, wert, label, ist_primaer, sortierung').eq('kanal', 'email').in('owner_id', ownerIds)
        : Promise.resolve({ data: [] as MailKanal[] }),
      supabase.from('kontakt_kanaele').select('owner_type, owner_id, wert, label, ist_primaer, sortierung').eq('kanal', 'email').ilike('wert', like).limit(10),
    ]);
    const kanaele = [...((eigeneRes.data || []) as MailKanal[]), ...((treffendRes.data || []) as MailKanal[])];
    const fremde = kanaele.filter((k) => !ownerIds.includes(k.owner_id));
    const namen = new Map<string, string>();
    if (fremde.length) {
      const nach = (t: KanalOwner) => fremde.filter((k) => k.owner_type === t).map((k) => k.owner_id);
      const [mN, eN, dN] = await Promise.all([
        nach('mieter').length ? supabase.from('mieter').select('id, vorname, nachname, firma_name').in('id', nach('mieter')) : Promise.resolve({ data: [] }),
        nach('eigentuemer').length ? supabase.from('eigentuemer').select('id, anrede, vorname, nachname, firma_name').in('id', nach('eigentuemer')) : Promise.resolve({ data: [] }),
        nach('dienstleister').length ? supabase.from('dienstleister').select('id, name, ansprechpartner').in('id', nach('dienstleister')) : Promise.resolve({ data: [] }),
      ]);
      (mN.data || []).forEach((m: any) => namen.set(m.id, `${m.vorname || ''} ${m.nachname || ''}`.trim() || m.firma_name || ''));
      (eN.data || []).forEach((e: any) => namen.set(e.id, eigentuemerAnzeigename(e)));
      (dN.data || []).forEach((d: any) => namen.set(d.id, d.name || d.ansprechpartner || ''));
    }
    setSearchResults(mitWeiterenAdressen(results, kanaele, namen) as SucheErgebnis[]);
  }, []);

  // Debounced search
  const handleSearchChange = useCallback((value: string, field: 'an' | 'cc' | 'bcc') => {
    if (field === 'an') setAnSearch(value);
    else if (field === 'cc') setCcSearch(value);
    else setBccSearch(value);

    setActiveField(field);

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchEmpfaenger(value);
    }, 300);
  }, [searchEmpfaenger]);

  // Add recipient
  const addEmpfaenger = useCallback((empf: Empfaenger, field: 'an' | 'cc' | 'bcc') => {
    const setter = field === 'an' ? setAn : field === 'cc' ? setCc : setBcc;
    setter(prev => {
      if (prev.some(e => e.email === empf.email)) return prev;
      return [...prev, empf];
    });
    if (field === 'an') setAnSearch('');
    else if (field === 'cc') setCcSearch('');
    else setBccSearch('');
    setSearchResults([]);
    setActiveField(null);
  }, []);

  // Remove recipient
  const removeEmpfaenger = useCallback((email: string, field: 'an' | 'cc' | 'bcc') => {
    const setter = field === 'an' ? setAn : field === 'cc' ? setCc : setBcc;
    setter(prev => prev.filter(e => e.email !== email));
  }, []);

  // Eine getippte Adresse ohne Kontakt ist ein gültiger Empfänger — sie war schon immer
  // per Eingabetaste möglich, nur sah man das nirgends (User-Frage 2026-08-12).
  const freieAdresse = (wert: string): boolean => adressenAusEingabe(wert).length > 0;

  // Enter/Paste: Eingabe zerlegen statt sie roh zu übernehmen. Eine eingefügte
  // Outlook-Liste („a@x.de; b@y.de") wird zu mehreren Chips, und ein anhängendes
  // Semikolon kommt gar nicht erst in den Chip — genau daran scheiterte der Versand
  // komplett (ErrorInvalidRecipients, 2026-08-13).
  const uebernehmeAdressen = useCallback((roh: string, field: 'an' | 'cc' | 'bcc'): boolean => {
    const adressen = adressenAusEingabe(roh);
    for (const adresse of adressen) addEmpfaenger({ email: adresse, name: adresse, typ: 'Team' }, field);
    return adressen.length > 0;
  }, [addEmpfaenger]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, field: 'an' | 'cc' | 'bcc') => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    uebernehmeAdressen(field === 'an' ? anSearch : field === 'cc' ? ccSearch : bccSearch, field);
  }, [anSearch, ccSearch, bccSearch, uebernehmeAdressen]);

  // Mehrere eingefügte Adressen sofort als Chips übernehmen; eine einzelne Adresse
  // bleibt normal im Feld stehen, damit die Kontaktsuche weiter greift.
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>, field: 'an' | 'cc' | 'bcc') => {
    const roh = e.clipboardData.getData('text');
    if (adressenAusEingabe(roh).length < 2) return;
    e.preventDefault();
    uebernehmeAdressen(roh, field);
  }, [uebernehmeAdressen]);

  // ─── Template Selection ───────────────────────────────────────────────────

  // Platzhalter-Werte ({{mieter.*}}, {{objekt.*}}, {{datum.heute}}, {{absender.*}}) für den
  // primären Empfänger laden. Deckt JEDEN Kontakt mit Anrede+Nachname ab: Mieter, Eigentümer
  // und allgemeine Kontakte — Abgleich über die E-Mail-Spalte UND über kontakt_kanaele
  // (auch Zweit-Adressen). Fehlende Werte -> '' (nichts Rohes landet in der Mail).
  const ladePlatzhalterWerte = async (): Promise<PlatzhalterWerte> => {
    const werte: PlatzhalterWerte = {
      'mieter.anrede': '', 'mieter.vorname': '', 'mieter.nachname': '', 'mieter.email': '', 'mieter.telefon': '',
      // Firma-bewusst: anrede_zeile = fertige Anrede (Firma → „Sehr geehrte Damen und Herren"),
      // firma = Firmenname, name = Anzeigename (Firma oder Person).
      'mieter.anrede_zeile': 'Sehr geehrte Damen und Herren,', 'mieter.firma': '', 'mieter.name': '',
      'objekt.bezeichnung': '', 'objekt.strasse': '', 'objekt.hausnummer': '', 'objekt.plz': '', 'objekt.ort': '',
      'datum.heute': new Date().toLocaleDateString('de-DE'),
      'absender.name': konto.bezeichnung || '', 'absender.email': konto.email_adresse || '',
    };
    const primaer = an[0]?.email;
    if (!primaer) return werte;
    werte['mieter.email'] = primaer;

    const setzePerson = (p: { anrede?: string | null; vorname?: string | null; nachname?: string | null; email?: string | null; telefon?: string | null; mobil?: string | null; firma_name?: string | null }) => {
      const firma = istFirma(p);
      werte['mieter.anrede'] = firma ? '' : (p.anrede ?? '');
      werte['mieter.vorname'] = p.vorname ?? '';
      werte['mieter.nachname'] = firma ? (p.firma_name?.trim() ?? '') : (p.nachname ?? '');
      werte['mieter.firma'] = p.firma_name?.trim() ?? '';
      werte['mieter.name'] = eigentuemerAnzeigename(p);
      werte['mieter.anrede_zeile'] = briefAnrede(p);
      werte['mieter.email'] = p.email ?? primaer;
      werte['mieter.telefon'] = p.telefon || p.mobil || '';
    };
    const setzeObjektAdresse = (a: { strasse?: string | null; hausnummer?: string | null; plz?: string | null }) => {
      werte['objekt.strasse'] = a.strasse ?? '';
      werte['objekt.hausnummer'] = a.hausnummer ?? '';
      werte['objekt.plz'] = a.plz ?? '';
    };

    const mieterCols = 'anrede, vorname, nachname, email, telefon, mobil, objekt_id';
    const eigCols = 'anrede, vorname, nachname, firma_name, email, telefon, mobil, strasse, hausnummer, plz';
    let objektId: string | null = null;
    let gefunden = false;

    // (a) Über kontakt_kanaele — deckt Haupt- UND Zweit-Mails von Mietern/Eigentümern ab.
    const { data: kanal } = await supabase.from('kontakt_kanaele')
      .select('owner_type, owner_id').eq('kanal', 'email').ilike('wert', primaer)
      .order('ist_primaer', { ascending: false }).limit(1).maybeSingle();
    if (kanal?.owner_type === 'mieter') {
      const { data } = await supabase.from('mieter').select(mieterCols).eq('id', kanal.owner_id).maybeSingle();
      if (data) { setzePerson(data); objektId = data.objekt_id ?? null; gefunden = true; }
    } else if (kanal?.owner_type === 'eigentuemer') {
      const { data } = await supabase.from('eigentuemer').select(eigCols).eq('id', kanal.owner_id).maybeSingle();
      if (data) { setzePerson(data); setzeObjektAdresse(data); gefunden = true; }
    }

    // (b) Fallback über direkte E-Mail-Spalten: Mieter -> Eigentümer -> allgemeine Kontakte.
    if (!gefunden) {
      const { data } = await supabase.from('mieter').select(mieterCols).ilike('email', primaer).maybeSingle();
      if (data) { setzePerson(data); objektId = data.objekt_id ?? null; gefunden = true; }
    }
    if (!gefunden) {
      const { data } = await supabase.from('eigentuemer').select(eigCols).ilike('email', primaer).maybeSingle();
      if (data) { setzePerson(data); setzeObjektAdresse(data); gefunden = true; }
    }
    if (!gefunden) {
      const { data } = await supabase.from('kontakte').select('anrede, vorname, nachname, email').ilike('email', primaer).maybeSingle();
      if (data) { setzePerson(data); gefunden = true; }
    }

    // Objekt (Bezeichnung/Adresse) über den Mieter.
    if (objektId) {
      const { data: o } = await supabase.from('objekte')
        .select('bezeichnung, strasse, hausnummer, plz').eq('id', objektId).maybeSingle();
      if (o) {
        werte['objekt.bezeichnung'] = o.bezeichnung ?? '';
        werte['objekt.strasse'] = o.strasse ?? '';
        werte['objekt.hausnummer'] = o.hausnummer ?? '';
        werte['objekt.plz'] = o.plz ?? '';
      }
    }
    return werte;
  };

  // extraWerte: zusätzliche Platzhalter-Werte (überlagern die empfänger-basierte Auflösung),
  // forceBetreff: Vorlagen-Betreff auch dann setzen, wenn schon einer vorbefüllt ist
  // (Auto-Anwendung: die Vorlage ist die Quelle, der Draft-Betreff nur Fallback).
  const handleVorlageChange = async (vorlageId: string, extraWerte?: Record<string, string> | null, forceBetreff = false) => {
    setSelectedVorlage(vorlageId);
    if (!vorlageId) return;
    const vorlage = vorlagen.find(v => v.id === vorlageId);
    if (!vorlage) return;
    // Ist schon ein Empfänger gesetzt: Platzhalter direkt auflösen (sichtbar im Editor).
    // Sonst literal einfügen — die Auflösung passiert spätestens beim Senden.
    const basis = an.length > 0 ? await ladePlatzhalterWerte() : null;
    // Mitgegebene Vorgangswerte (autoVorlageWerte, z. B. Ticket-/Auftragsdaten) gelten für JEDE
    // Vorlage dieses Fensters — auch wenn der Nutzer nachträglich eine andere auswählt. Vorher
    // waren sie nur an die Auto-Vorlage gebunden: wer im Beauftragen-Fenster auf „Angebotsanfrage"
    // umstellte, bekam eine Vorlage mit lauter leeren Feldern (Objekt, Einheit, Mangel).
    const vorgang = autoVorlageWerte ?? null;
    const zusammen = { ...(basis ?? {}), ...(vorgang ?? {}), ...(extraWerte ?? {}) };
    const werte = Object.keys(zusammen).length > 0 ? zusammen : null;
    if (vorlage.betreff_template && (forceBetreff || !betreff)) {
      const b = werte ? ersetzePlatzhalter(vorlage.betreff_template, werte) : vorlage.betreff_template;
      setBetreff(betreffMitZusatz(b, betreffZusatz));
    }
    // Vorlagen-HTML säubern (Staff-Inhalt → gespeichertes XSS möglich), Platzhalter auflösen,
    // dann in den aktiven Editor: bei Briefpapier in die Textzone im Bogen, sonst in den Text-Editor.
    let html = sanitizeHtml(vorlage.html_template ?? '');
    if (werte) html = ersetzePlatzhalter(html, werte);
    inhaltCacheRef.current = html;
    if (aktivesBriefpapier) {
      renderBriefEditor(html);
    } else if (editorRef.current) {
      editorRef.current.innerHTML = html;
    }
  };

  // Vorausgefüllte Aktion (z. B. „Rechnung an Eigentümer"): die Vorlage mit dem angegebenen
  // ki_trigger genau wie eine manuelle Auswahl anwenden. ERST wenn Vorlagen + Signaturen +
  // Briefpapier vollständig geladen sind (sonst überschreiben die Signatur-/Briefpapier-Renders
  // den Vorlagentext). Doppeltes rAF stellt sicher, dass unser Render der letzte ist.
  const autoVorlageRef = useRef(false);
  useEffect(() => {
    if (!autoVorlageTrigger || autoVorlageRef.current) return;
    if (!vorlagenGeladen || !signaturenGeladen || !briefpapierGeladen) return;
    // Ohne Empfänger keine Auflösung möglich — außer die Werte kommen explizit mit
    // (z. B. Handwerker-Beauftragung: Ticket-Daten statt Empfänger-Lookup).
    if (an.length === 0 && !autoVorlageWerte) return;
    const v = vorlagen.find((x) => x.ki_trigger === autoVorlageTrigger);
    if (!v) { autoVorlageRef.current = true; return; }        // keine passende Vorlage → aufgeben
    autoVorlageRef.current = true;
    requestAnimationFrame(() => requestAnimationFrame(() => { void handleVorlageChange(v.id, autoVorlageWerte ?? null, true); }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoVorlageTrigger, autoVorlageWerte, vorlagenGeladen, signaturenGeladen, briefpapierGeladen, an, vorlagen]);

  // Vorbereiteter Freitext (autoText, z. B. Auftragstext): gleiche Mechanik wie autoVorlage —
  // erst wenn Signaturen + Briefpapier geladen sind, doppeltes rAF, dann über den aktiven
  // Editor-Pfad anwenden (Bogen-Textzone bzw. Plain-Editor).
  const autoTextRef = useRef(false);
  useEffect(() => {
    if (!autoText || autoTextRef.current) return;
    if (!signaturenGeladen || !briefpapierGeladen) return;
    // Greift eine Auto-Vorlage (autoVorlageTrigger mit vorhandener Vorlage), ist SIE die
    // Quelle — autoText ist dann nur der Fallback für fehlende/inaktive Vorlagen.
    if (autoVorlageTrigger) {
      if (!vorlagenGeladen) return;
      if (vorlagen.some((x) => x.ki_trigger === autoVorlageTrigger)) return;
    }
    autoTextRef.current = true;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const html = textZuHtml(autoText);
      inhaltCacheRef.current = html;
      if (aktivesBriefpapier) {
        renderBriefEditor(html);
      } else if (editorRef.current) {
        editorRef.current.innerHTML = html;
      }
      setBodyDirty((n) => n + 1);
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoText, signaturenGeladen, briefpapierGeladen, vorlagenGeladen, vorlagen, autoVorlageTrigger]);

  // ─── File Upload ──────────────────────────────────────────────────────────

  const uploadFiles = async (files: FileList | File[]) => {
    setUploading(true);
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      // Schluessel ohne Umlaute: Storage lehnt "Kündigungsbestätigung.pdf" sonst ab
      // (Ticket #480). Der Anzeigename unten bleibt file.name — so sieht ihn der Empfaenger.
      const filePath = `${konto.id}/${Date.now()}_${speicherName(file.name)}`;
      const { data, error: uploadError } = await supabase.storage
        .from('email-anhaenge')
        .upload(filePath, file);

      if (uploadError) {
        setError(`Upload fehlgeschlagen: ${file.name}`);
        continue;
      }

      setAnhaenge(prev => [
        ...prev,
        {
          id: crypto.randomUUID(),
          name: file.name,
          size: file.size,
          path: data.path,
        },
      ]);
    }
    setUploading(false);
  };

  // #546: Anhang im Entwurf öffnen (Vorschau im neuen Tab) — die Kacheln hatten nur „entfernen“.
  const oeffneAnhang = async (anhang: Anhang) => {
    if (!anhang.path) return;
    const fenster = window.open('', '_blank');
    const { data, error: urlErr } = await supabase.storage.from('email-anhaenge').createSignedUrl(anhang.path, 300);
    if (urlErr || !data?.signedUrl) { fenster?.close(); setError('Anhang konnte nicht geöffnet werden.'); return; }
    if (fenster) fenster.location.href = data.signedUrl; else window.location.assign(data.signedUrl);
  };

  const removeAnhang = async (anhang: Anhang) => {
    if (!anhang.isDraft) {
      await supabase.storage.from('email-anhaenge').remove([anhang.path]);
    }
    setAnhaenge(prev => prev.filter(a => a.id !== anhang.id));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  // ─── Anhang aus dem Aktenschrank (Google Drive) ───────────────────────────

  const handleAktenSuche = (q: string) => {
    setAktenSuche(q);
    if (aktenTimer.current) clearTimeout(aktenTimer.current);
    if (q.trim().length < 2) { setAktenTreffer([]); setAktenLaedt(false); return; }
    setAktenLaedt(true);
    aktenTimer.current = setTimeout(async () => {
      try { setAktenTreffer(await sucheDateien(q.trim())); }
      catch { setAktenTreffer([]); }
      finally { setAktenLaedt(false); }
    }, 350);
  };

  // Ordner-Browser: Inhalt eines Drive-Ordners laden ('' = Root/Aktenschrank).
  const ladeAktenOrdner = async (id: string) => {
    setAktenBrowseLaedt(true);
    try { setAktenInhalt(await ladeOrdnerInhalt(id)); }
    catch { setAktenInhalt([]); }
    finally { setAktenBrowseLaedt(false); }
  };
  const oeffneAktenPicker = () => {
    const willOpen = !showAkten;
    setShowAkten(willOpen);
    if (willOpen && !aktenInitRef.current) { aktenInitRef.current = true; ladeAktenOrdner(aktenStartOrdner?.id ?? ''); }
  };
  const oeffneUnterordner = (e: DriveEintrag) => {
    setAktenStack((prev) => [...prev, { id: e.id, name: e.name }]);
    ladeAktenOrdner(e.id);
  };
  const springeZuBreadcrumb = (index: number) => {
    setAktenStack((prev) => {
      const neu = prev.slice(0, index + 1);
      ladeAktenOrdner(neu[neu.length - 1].id);
      return neu;
    });
  };

  // Drive-Datei laden -> in den email-anhaenge-Bucket kopieren -> als Anhang übernehmen.
  const haengeDateiAn = async (id: string, name: string, mimeType: string) => {
    if (istGoogleFormat(mimeType)) { setError('Google-Dokumente (Docs/Sheets) können nicht direkt angehängt werden.'); return; }
    setAktenHolt(id);
    setError(null);
    try {
      const blob = await ladeDateiBlob(id, mimeType);
      const path = `${konto.id}/${Date.now()}_${speicherName(name)}`;
      const { error: upErr } = await supabase.storage.from('email-anhaenge').upload(path, blob, { contentType: blob.type || 'application/octet-stream' });
      if (upErr) throw upErr;
      setAnhaenge(prev => [...prev, { id: crypto.randomUUID(), name, size: blob.size, path }]);
    } catch (e: any) {
      setError(e?.message || 'Anhängen aus dem Aktenschrank fehlgeschlagen.');
    } finally {
      setAktenHolt(null);
    }
  };

  // ─── Send ─────────────────────────────────────────────────────────────────

  // Gewählte Signatur als gesäubertes HTML (leer = keine).
  const chosenSignaturHtml = (): string => {
    const s = signaturen.find((x) => x.id === signaturId);
    return s ? sanitizeHtml(s.html_signatur) : '';
  };

  const aktivesBriefpapier = briefpapier.find((b) => b.id === briefpapierId) || null;

  // Fertige Mail für den Versand zusammensetzen (reines Template, ohne Editor-Attribute):
  //  - mit Briefpapier: Text in {{inhalt}}, Signatur in {{signatur}}
  //  - ohne Briefpapier: Signatur (falls gewählt) unten anhängen
  const baueMailHtml = (userHtml: string): string => {
    const sig = chosenSignaturHtml();
    const sigMitZitat = sig + sanitizeHtml(aktuellerZitatHtml());
    const clean = sanitizeHtml(userHtml);
    if (!aktivesBriefpapier) {
      return sigMitZitat ? `${clean}<div><br></div><div><br></div>${sigMitZitat}` : clean;
    }
    let html = ohneDoppeltenGruss(aktivesBriefpapier.html, sig);
    html = html.includes('{{inhalt}}') ? html.split('{{inhalt}}').join(clean) : html.replace('</body>', `${clean}</body>`);
    html = html.includes('{{signatur}}') ? html.split('{{signatur}}').join(sigMitZitat) : (sigMitZitat ? html.replace('</body>', `${sigMitZitat}</body>`) : html);
    return html;
  };

  // ─── Entwurf: Auto-Speichern (ordner=DRAFT) ───────────────────────────────

  // Aktueller Nutzertext (reiner Body, ohne Briefpapier/Signatur/Zitat) aus dem aktiven Editor.
  const aktuellerBodyHtml = (): string =>
    (aktivesBriefpapier
      ? (briefEditorRef.current?.contentDocument?.getElementById('ce-inhalt')?.innerHTML ?? inhaltCacheRef.current)
      : (editorRef.current?.innerHTML ?? inhaltCacheRef.current)) || '';

  // Aktive Zitat-Zone lesen; „leere" Karte (Kopfzeile ohne Inhalt) zählt als kein Zitat.
  const aktuellerZitatHtml = (): string => {
    const roh = aktivesBriefpapier
      ? (briefEditorRef.current?.contentDocument?.getElementById('ce-zitat')?.innerHTML ?? zitatCacheRef.current)
      : (zitatEditorRef.current?.innerHTML ?? zitatCacheRef.current);
    const wert = (roh || '').trim();
    if (!wert || !wert.includes('data-zitat')) return '';
    const nachMarker = wert.includes('data-zitat-inhalt') ? wert.split('data-zitat-inhalt')[1] : '';
    // Erst NACH dem schließenden „>" des Attribut-Tags weiterlesen — sonst hängt das
    // Attribut-Fragment (`="1" style="…">`) selbst als „Text" am Inhalt und die Karte
    // zählt fälschlich nie als leer.
    const inhaltText = nachMarker ? htmlZuText(nachMarker.slice(nachMarker.indexOf('>') + 1)).trim() : '';
    const inhaltLeer = !wert.includes('data-zitat-inhalt') || !inhaltText;
    return inhaltLeer ? '' : wert;
  };

  // Nur Anzeige im Schreibfenster: Uhrzeit des letzten erfolgreichen Auto-Speicherns.
  const [gespeichertUm, setGespeichertUm] = useState<string | null>(null);
  // Schreibfenster: minimiert (Leiste unten rechts) und „Original“-Spalte.
  const [minimiert, setMinimiert] = useState(false);
  // Rechter Rand der Sidebar – das Schreibfenster liegt wie im Prototyp über der Inhaltsfläche daneben.
  const inhaltLinks = useInhaltsflaecheLinks();
  const [originalAn, setOriginalAn] = useState(false);
  const merkeGespeichert = () => setGespeichertUm(new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));

  const speichereEntwurf = async () => {
    if (sentRef.current || speicherLaeuftRef.current || !userIdRef.current) return;
    const html = aktuellerBodyHtml();
    const zitat = aktuellerZitatHtml();
    const text = htmlZuText(html);
    const anEmails = an.map((e) => e.email);
    // Leere Mail nicht als Entwurf speichern.
    if (anEmails.length === 0 && !betreff.trim() && !text.trim() && anhaenge.length === 0) return;
    speicherLaeuftRef.current = true;
    const zeile = {
      konto_id: konto.id,
      user_id: userIdRef.current,
      richtung: 'ausgang',
      ordner: 'DRAFT',
      von: konto.email_adresse,
      an: anEmails,
      cc: cc.map((e) => e.email),
      bcc: bcc.map((e) => e.email),
      betreff: betreff.trim(),
      html_inhalt: html + zitat,
      text_inhalt: text,
      hat_anhaenge: anhaenge.length > 0,
      entwurf_anhaenge: anhaenge.map((a) => ({ name: a.name, size: a.size, path: a.path })),
      datum: new Date().toISOString(),
      gelesen: true,
    };
    try {
      // supabase-js wirft NICHT — ohne diese Fehlerprüfung schlug das Speichern still fehl.
      // Genau das verdeckte monatelang die zu enge INSERT-Regel auf emails: Entwürfe wurden
      // für alle außer dem Postfach-Eigentümer nie gespeichert, sichtbar wurde es erst beim
      // geplanten Senden. Der Entwurf bleibt eine Hintergrundaktion — deshalb Konsole statt Banner.
      if (draftIdRef.current) {
        const { error } = await supabase.from('emails').update(zeile).eq('id', draftIdRef.current);
        if (error) console.warn('Entwurf speichern fehlgeschlagen:', error.message);
        else merkeGespeichert();
      } else {
        const { data, error } = await supabase.from('emails').insert(zeile).select('id').single();
        if (error) console.warn('Entwurf speichern fehlgeschlagen:', error.message);
        if (data) { draftIdRef.current = data.id as string; merkeGespeichert(); }
      }
    } catch (e) {
      console.warn('Entwurf speichern fehlgeschlagen', e);
    } finally {
      speicherLaeuftRef.current = false;
    }
  };

  const planeEntwurf = () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { void speichereEntwurf(); }, 1500);
  };

  // Nach jeder echten Änderung (nicht beim Initial-Render) Auto-Speichern einplanen.
  useEffect(() => {
    if (ersterAutosaveRef.current) { ersterAutosaveRef.current = false; return; }
    planeEntwurf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [an, cc, bcc, betreff, anhaenge, bodyDirty]);

  // Beim Schließen (Unmount) den letzten Stand sichern — jeweils neueste Fassung via Ref.
  const speichernRef = useRef(speichereEntwurf);
  speichernRef.current = speichereEntwurf;
  useEffect(() => () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    void speichernRef.current();
  }, []);

  // Versandfertig-Prüfung: Empfänger + Betreff müssen stehen — von handleSend UND
  // handleSpaeterSenden genutzt, damit beide Wege identisch validieren.
  // Bcc allein reicht — Sammelanfragen (z. B. an mehrere Handwerker) haben oft
  // KEINEN An-Empfänger. Regeln in lib/emails/versand.ts (getestet).
  const pruefeVersandfertig = (): string | null => versandFehler(an, cc, bcc, betreff);

  // Der EINE Renderer für den fertigen Mail-Inhalt: Platzhalter final auflösen (falls die
  // Vorlage ohne gesetzten Empfänger eingefügt wurde oder der Empfänger danach geändert
  // wurde — verbleibende {{...}} werden entfernt) und Briefpapier/Signatur einsetzen.
  // handleSend UND handleSpaeterSenden rufen exakt diese Funktion, damit eine geplante Mail
  // byte-identisch zu dem wird, was ein Sofort-Versand erzeugt hätte.
  const renderiereFertigeMail = async (): Promise<{ html: string; text: string; betreff: string }> => {
    const werte = await ladePlatzhalterWerte();
    const userHtml = aktivesBriefpapier
      ? (briefEditorRef.current?.contentDocument?.getElementById('ce-inhalt')?.innerHTML ?? inhaltCacheRef.current)
      : (editorRef.current?.innerHTML || '');
    const html = baueMailHtml(ersetzePlatzhalter(userHtml, werte));
    const finalBetreff = ersetzePlatzhalter(betreff.trim(), werte);
    const text = htmlZuText(html);
    return { html, text, betreff: finalBetreff };
  };

  // Mailtext (Platzhalter gefüllt, ohne Mail-Briefpapier/Signatur – das Anschreiben bekommt das echte Briefpapier)
  // + Anhänge an „Per Post senden“ übergeben. Empfänger: erste An-Adresse, falls sie zu einem Kontakt gehört.
  const alsBrief = async () => {
    setError(null); setPostMeldung(null);
    try {
      const werte = await ladePlatzhalterWerte();
      const userHtml = aktivesBriefpapier
        ? (briefEditorRef.current?.contentDocument?.getElementById('ce-inhalt')?.innerHTML ?? inhaltCacheRef.current)
        : (editorRef.current?.innerHTML || '');
      const text = briefTextAusHtml(ersetzePlatzhalter(userHtml, werte));
      const treffer = an[0]?.email ? await findeEmpfaengerZuEmail(an[0].email) : null;
      setPerPost({
        betreff: ersetzePlatzhalter(betreff.trim(), werte), text, treffer,
        anlagen: anhaenge.map((a) => ({ name: a.name, mime: mimeAusName(a.name), laden: () => ladeMailAnhang(a.path), quelle: { storage: { bucket: 'email-anhaenge', path: a.path } } })),
      });
    } catch (e) { setError(`Brief nicht vorbereitet: ${(e as Error).message}`); }
  };

  const handleSend = async () => {
    const fehlerText = pruefeVersandfertig();
    if (fehlerText) { setError(fehlerText); return; }

    setSending(true);
    setError(null);
    // #546: Autosave VOR dem Versand sperren und einen laufenden abwarten — sonst legte ein
    // gerade laufendes Speichern den Entwurf NACH dem Löschen neu an (verwaiste Entwürfe).
    sentRef.current = true;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    await wartAufLaufendenAutosave();

    const { html, text: finalText, betreff: finalBetreff } = await renderiereFertigeMail();
    try {
      const { data: sendData, error: sendError } = await supabase.functions.invoke('graph-mail-send', {
        body: {
          konto_id: konto.id,
          an: an.map(e => e.email),
          cc: cc.map(e => e.email),
          bcc: bcc.map(e => e.email),
          betreff: finalBetreff,
          html_inhalt: html,
          text_inhalt: finalText,
          anhaenge: anhaenge.map(a => ({ name: a.name, path: a.path })),
          in_reply_to: replyTo?.message_id || null,
        },
      });

      if (sendError) throw new Error(await funktionsFehlerText(sendError));
      // Erfolgreich versendet: Entwurf entfernen (Autosave ist seit Beginn gesperrt).
      if (draftIdRef.current) {
        // #567: Löschen ohne Recht verwirft die DB still (0 Zeilen) — sichtbar machen statt verschlucken.
        const { data: weg, error: wegFehler } = await supabase.from('emails').delete().eq('id', draftIdRef.current).select('id');
        if (wegFehler || !weg?.length) console.warn('Entwurf nach dem Senden nicht gelöscht', wegFehler?.message ?? 'keine Berechtigung');
        draftIdRef.current = null;
      }
      // #575: ältere eigene Entwürfe derselben Mail (Fenster zwischendurch geschlossen) ebenfalls weg
      await raeumeUeberholteEntwuerfeAuf(konto.id, userIdRef.current, { betreff: betreff.trim(), an: an.map((e) => e.email) }, null);
      onSent({
        betreff: finalBetreff,
        text: finalText,
        an: an.map((e) => e.email),
        cc: cc.map((e) => e.email),
        bcc: bcc.map((e) => e.email),
        anhaenge: anhaenge.map(a => ({ name: a.name, size: a.size, path: a.path })),
        // ID der Sent-Zeile — Ticket-Flows verknüpfen darüber die Mail statt den
        // Volltext als Kommentar zu spiegeln.
        emailId: (sendData as { email_id?: string | null } | null)?.email_id ?? null,
      });
    } catch (err: any) {
      sentRef.current = false; // Versand fehlgeschlagen — Entwurf darf wieder gespeichert werden
      setError(err?.message || 'Fehler beim Senden der E-Mail.');
    } finally {
      setSending(false);
    }
  };

  // Erfolgsverhalten von handleSend gespiegelt — ABER kein echter Versand, daher onSent NICHT
  // mit Versand-Info aufrufen (das würde z. B. eine Ticket-Spiegelung als bereits verschickte
  // Nachricht auslösen). Ein eigener Toast-/Notify-Mechanismus existiert in dieser Komponente
  // nicht — die Liste aktualisiert sich über den bestehenden onClose-Flow.
  const notifyOderClose = () => {
    onClose();
  };

  // Wartet, falls GERADE ein Autosave in Flight ist (speicherLaeuftRef) — der `sentRef`-Guard
  // in speichereEntwurf schützt nur vor NEUEN Autosaves, nicht vor einem bereits laufenden
  // (der hat den Guard schon vor unserem Sperren passiert). Kurzes Polling reicht, da
  // speichereEntwurf ein einzelner Netzwerk-Call ist (üblicherweise < 1s); Timeout als
  // Sicherheitsnetz, falls der Call ungewöhnlich lange hängt.
  const wartAufLaufendenAutosave = async (): Promise<void> => {
    const start = Date.now();
    while (speicherLaeuftRef.current && Date.now() - start < 5000) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  };

  // Später senden: Mail exakt wie beim Sofort-Versand rendern (renderiereFertigeMail) und als
  // SCHEDULED speichern — der Versand-Cron (email-geplant-versand) verschickt sie im
  // 5-Minuten-Raster. Ein vorhandener Entwurf wird zur SCHEDULED-Zeile (UPDATE), nicht dupliziert.
  const handleSpaeterSenden = async (datumIso: string) => {
    const fehlerText = pruefeVersandfertig();
    if (fehlerText) { setError(fehlerText); return; }
    // Sofort synchron sperren — VOR jedem await —, damit ein zeitgleich feuernder debounced
    // Autosave (1,5-s-Timer) die Zeile nicht mehr als DRAFT zurückschreiben kann, während wir
    // sie auf SCHEDULED umstellen (Race: ohne dieses frühe Sperren könnte ein Autosave, dessen
    // sentRef-Check noch VOR unserem Schreiben lief, nach unserem SCHEDULED-Update noch
    // ordner='DRAFT' überschreiben — je nach Antwortreihenfolge der beiden Requests). Bei
    // Fehlschlag im catch wieder freigeben, damit der Autosave danach normal weiterläuft.
    sentRef.current = true;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setSending(true);
    setError(null);
    try {
      // Einen bereits laufenden Autosave abwarten (siehe Kommentar oben an
      // wartAufLaufendenAutosave) — schließt die verbleibende Lücke des synchronen Sperrens.
      await wartAufLaufendenAutosave();
      // Roher Editor-Body + Zitat-Karte (ohne Briefpapier/Signatur, dieselbe Quelle wie
      // speichereEntwurf) zusätzlich sichern — falls "Abbrechen" die Zeile später wieder zu
      // einem DRAFT macht, liest EmailListe diesen rohen Stand statt den fertig gerenderten
      // html_inhalt erneut durchs Briefpapier zu jagen (sonst doppelte Signatur/verschachteltes
      // Briefpapier).
      const entwurfHtml = aktuellerBodyHtml() + aktuellerZitatHtml();
      const { html, text, betreff: finalBetreff } = await renderiereFertigeMail();
      const zeile = {
        konto_id: konto.id,
        user_id: userIdRef.current,
        richtung: 'ausgang' as const,
        ordner: 'SCHEDULED',
        geplant_am: datumIso,
        von: konto.email_adresse,
        an: an.map((e) => e.email),
        cc: cc.map((e) => e.email),
        bcc: bcc.map((e) => e.email),
        betreff: finalBetreff,
        html_inhalt: html,
        entwurf_html: entwurfHtml,
        text_inhalt: text,
        in_reply_to: replyTo?.message_id || null,
        hat_anhaenge: anhaenge.length > 0,
        entwurf_anhaenge: anhaenge.map((a) => ({ name: a.name, size: a.size, path: a.path })),
        datum: datumIso,
        gelesen: true,
      };
      if (draftIdRef.current) {
        const { error: err } = await supabase.from('emails').update(zeile).eq('id', draftIdRef.current);
        if (err) throw new Error(err.message);
      } else {
        const { error: err } = await supabase.from('emails').insert(zeile);
        if (err) throw new Error(err.message);
      }
      // #575: ältere eigene Entwürfe derselben Mail entfernen (der eigene ist jetzt die geplante Mail)
      await raeumeUeberholteEntwuerfeAuf(konto.id, userIdRef.current, { betreff: betreff.trim(), an: an.map((e) => e.email) }, draftIdRef.current);
      draftIdRef.current = null;
      notifyOderClose();
    } catch (e) {
      sentRef.current = false; // Planen fehlgeschlagen — Autosave darf wieder greifen
      setError((e as Error).message || 'Planen fehlgeschlagen.');
    } finally {
      setSending(false);
    }
  };

  // ─── KI im Composer ────────────────────────────────────────────────────────

  // Button „Meinen Text überarbeiten": nimmt den aktuellen eigenen Text (ohne Zitat) und
  // schickt ihn mit der Anweisung an die KI. Funktioniert in JEDEM Modus (auch bei neuen Mails).
  const kiTextUeberarbeiten = async () => {
    setKiFehler(null);
    const eigener = htmlZuText(aktuellerBodyHtml()).trim();
    if (!eigener) { setKiFehler('Kein eigener Text zum Überarbeiten vorhanden.'); return; }
    setKiLaedt(true);
    try {
      const { data, error: err } = await supabase.functions.invoke('email-antwort-entwurf', {
        body: { aktion: 'ueberarbeiten', text: eigener, anweisung: kiAnweisung },
      });
      if (err) throw err;
      const entwurf = (data as { entwurf?: string; error?: string } | null)?.entwurf;
      if (!entwurf) throw new Error((data as { error?: string } | null)?.error || 'Leere Antwort erhalten.');
      setKiVorschau(entwurf);
    } catch (e: any) {
      setKiFehler(e?.message || 'KI-Anfrage fehlgeschlagen.');
    } finally {
      setKiLaedt(false);
    }
  };

  // Button „Antwort entwerfen" (nur Antwort-Modus): nutzt den bestehenden email_id-Pfad,
  // der die Original-Mail + KI-Analyse einbezieht; ein bereits geschriebener eigener Text
  // und die Anweisung fließen zusätzlich mit ein.
  const kiAntwortEntwerfen = async () => {
    if (!replyTo) return;
    setKiFehler(null);
    const eigener = htmlZuText(aktuellerBodyHtml()).trim();
    setKiLaedt(true);
    try {
      const { data, error: err } = await supabase.functions.invoke('email-antwort-entwurf', {
        body: { email_id: replyTo.id, anweisung: kiAnweisung, eigener_text: eigener || undefined },
      });
      if (err) throw err;
      const entwurf = (data as { entwurf?: string; error?: string } | null)?.entwurf;
      if (!entwurf) throw new Error((data as { error?: string } | null)?.error || 'Leere Antwort erhalten.');
      setKiVorschau(entwurf);
    } catch (e: any) {
      setKiFehler(e?.message || 'KI-Anfrage fehlgeschlagen.');
    } finally {
      setKiLaedt(false);
    }
  };

  // Übernehmen: KI-Vorschau als neuer eigener Text in den aktiven Editor — die Zitat-Zone
  // wird NICHT angefasst (getrennt geführt, siehe zitatHtml/zitatCacheRef).
  const kiVorschauUebernehmen = () => {
    if (kiVorschau == null) return;
    const html = textZuHtml(kiVorschau);
    if (aktivesBriefpapier) {
      const frame = briefEditorRef.current;
      const ce = frame?.contentDocument?.getElementById('ce-inhalt');
      if (ce) {
        ce.innerHTML = html;
        // Bogen-Höhe an den neuen Inhalt anpassen (sonst evtl. abgeschnitten).
        if (frame?.contentDocument?.body) frame.style.height = `${frame.contentDocument.body.scrollHeight + 8}px`;
      }
    } else if (editorRef.current) {
      editorRef.current.innerHTML = html;
    }
    inhaltCacheRef.current = html;
    setBodyDirty((n) => n + 1); // Autosave-Trigger, bestehendes Muster
    setKiVorschau(null);
    setKiAnweisung('');
    setShowKi(false);
  };

  // Beim ersten Öffnen des KI-Panels klären, ob der Stift erscheint (nur Admins).
  useEffect(() => {
    if (!showKi || kiDarfRegeln) return;
    let aktiv = true;
    darfPromptAendern().then((darf) => { if (aktiv) setKiDarfRegeln(darf); }).catch(() => {});
    return () => { aktiv = false; };
  }, [showKi, kiDarfRegeln]);

  const kiRegelnOeffnen = async () => {
    setKiFehler(null);
    try {
      setKiRegeln(await ladePrompt());
      setKiRegelnOffen(true);
    } catch (e: any) {
      setKiFehler(e?.message || 'Regeln konnten nicht geladen werden.');
    }
  };

  const kiRegelnSpeichern = async () => {
    setKiRegelnSpeichert(true);
    setKiFehler(null);
    try {
      await speicherePrompt(kiRegeln);
      setKiRegelnOffen(false);
    } catch (e: any) {
      setKiFehler(e?.message || 'Regeln konnten nicht gespeichert werden.');
    } finally {
      setKiRegelnSpeichert(false);
    }
  };

  const kiVorschauVerwerfen = () => {
    setKiVorschau(null);
    setKiFehler(null);
  };

  // ─── Rich-Text-Editor: Init + Format-Toolbar ──────────────────────────────

  // Editor mit Ausgangsinhalt füllen: gespeicherter Entwurf (HTML, OHNE Zitat-Karte — die
  // lebt separat in zitatHtml/zitatCacheRef) hat Vorrang, sonst der Draft-Text.
  useEffect(() => {
    // Derselbe Split wie im zitatHtml-Initializer (entwurfZerlegung(), gecacht in
    // entwurfZerlegungRef) — ein zitatloser Entwurf liefert eigenerHtml UNVERÄNDERT (kein
    // Plaintext-Roundtrip über htmlZuText/textZuHtml, der Formatierung wie Fett/Listen/Links/
    // Bilder plätten würde); nur mit Zitat-Karte oder Alt-Plaintext-Marker wird konvertiert.
    const initHtml = draft?.html_inhalt ? sanitizeHtml(entwurfZerlegung().eigenerHtml) : textZuHtml(inhalt);
    if (editorRef.current) editorRef.current.innerHTML = initHtml;
    inhaltCacheRef.current = editorRef.current?.innerHTML ?? initHtml;
    // Defense-in-depth: zitatCacheRef ist bereits an der Quelle gesäubert (siehe zitatHtml-Init),
    // hier zusätzlich vor dem Einsetzen ins DOM — schützt auch, falls der Cache je aus einer
    // anderen, ungesäuberten Quelle befüllt wird.
    if (zitatEditorRef.current) zitatEditorRef.current.innerHTML = sanitizeHtml(zitatCacheRef.current);
    supabase.auth.getUser().then(({ data }) => { userIdRef.current = data.user?.id ?? null; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Briefpapier-WYSIWYG (direkt im Bogen schreiben, Signatur unten sichtbar) ──

  // Aktive Formate der aktuellen Auswahl (für die leuchtenden Toolbar-Knöpfe).
  const BRIEF_CMDS = ['bold', 'italic', 'underline', 'strikeThrough', 'insertUnorderedList', 'insertOrderedList', 'justifyLeft', 'justifyCenter', 'justifyRight'];
  const aktualisiereBriefFormat = () => {
    const d = briefEditorRef.current?.contentDocument;
    if (!d) return;
    const s: Record<string, boolean> = {};
    for (const c of BRIEF_CMDS) { try { s[c] = d.queryCommandState(c); } catch { s[c] = false; } }
    setBriefFormat(s);
  };

  // Editier-Dokument bauen: {{inhalt}} -> editierbare Zone, {{signatur}} -> Signatur + Zitat-Karte.
  const CE_CSS = '<style>#ce-inhalt{outline:none;min-height:90px;}#ce-inhalt:empty:before{content:attr(data-ph);color:#b8b8b8;}#ce-sig{margin-top:2px;}#ce-zitat{outline:none;}</style>';
  // zitatHtmlWert === null (kein Zitat in dieser Mail, siehe zitatHtml-State) -> die #ce-zitat-
  // Zone entfällt komplett (nicht nur leer) — sonst bliebe ein unsichtbares, aber tippbares
  // contentEditable-Feld übrig, dessen Inhalt beim Auslesen mangels data-zitat-Kennung verlorenginge.
  const buildBriefDoc = (bpHtml: string, inhaltHtml: string, sigHtml: string, zitatHtmlWert: string | null): string => {
    const zone = `<div id="ce-inhalt" contenteditable="true" data-ph="Hier Ihren Text schreiben …">${inhaltHtml || ''}</div>`;
    const sig = `<div id="ce-sig">${sigHtml || ''}</div>`;
    // Zitat-Karte direkt HINTER der Signatur — Reihenfolge Text -> Signatur -> Zitat.
    const zitatBlock = zitatHtmlWert !== null ? `<div id="ce-zitat" contenteditable="true">${zitatHtmlWert || ''}</div>` : '';
    let doc = ohneDoppeltenGruss(bpHtml, sigHtml);
    doc = doc.includes('{{inhalt}}') ? doc.split('{{inhalt}}').join(zone) : doc.replace('</body>', `${zone}</body>`);
    doc = doc.includes('{{signatur}}') ? doc.split('{{signatur}}').join(sig + zitatBlock) : doc.replace('</body>', `${sig}${zitatBlock}</body>`);
    const inject = `${CE_CSS}<base target="_blank">`;
    return doc.includes('</head>') ? doc.replace('</head>', `${inject}</head>`) : inject + doc;
  };

  // Dokument in die iframe schreiben + Auto-Höhe + Input-/Link-Listener einrichten.
  const renderBriefEditor = (inhaltHtml: string) => {
    const bp = briefpapier.find((b) => b.id === briefpapierId);
    const frame = briefEditorRef.current;
    if (!bp || !frame) return;
    const doc = frame.contentDocument;
    if (!doc) return;
    doc.open();
    // Defense-in-depth (siehe Mount-Effekt): zitatCacheRef ist bereits an der Quelle gesäubert,
    // hier zusätzlich vor dem doc.write() ins iframe-Dokument.
    doc.write(buildBriefDoc(bp.html, inhaltHtml, chosenSignaturHtml(), zitatHtml !== null ? sanitizeHtml(zitatCacheRef.current) : null));
    doc.close();
    // Formatierung als Inline-CSS (span style=…) statt veralteter <font>-Tags — E-Mail-robust.
    try { doc.execCommand('styleWithCSS', false, 'true'); } catch { /* egal */ }
    const passHoehe = () => { if (frame.contentDocument?.body) frame.style.height = `${frame.contentDocument.body.scrollHeight + 8}px`; };
    const ce = doc.getElementById('ce-inhalt');
    ce?.addEventListener('input', () => { inhaltCacheRef.current = ce.innerHTML; setBodyDirty((n) => n + 1); passHoehe(); });
    // Zitat-Zone: eigener Listener, Cache statt State pro Tastendruck (Muster wie #ce-inhalt).
    const zi = doc.getElementById('ce-zitat');
    zi?.addEventListener('input', () => { zitatCacheRef.current = zi.innerHTML; setBodyDirty((n) => n + 1); passHoehe(); });
    // Auswahl-Änderungen -> aktive Format-Knöpfe aktualisieren.
    doc.addEventListener('selectionchange', aktualisiereBriefFormat);
    // Nur der Textbereich ist editierbar — Klicks auf Kopf-/Fuß-Links nicht navigieren lassen.
    doc.addEventListener('click', (e) => { const a = (e.target as HTMLElement)?.closest?.('a'); if (a) e.preventDefault(); });
    // Höhe nachziehen, sobald (Base64-)Bilder wie Logo/Signaturfoto fertig geladen sind.
    Array.from(doc.images || []).forEach((img) => { if (!img.complete) img.addEventListener('load', passHoehe); });
    passHoehe();
    setTimeout(passHoehe, 60);
    setTimeout(passHoehe, 300);
  };

  // Briefpapier-/Signatur-Wechsel: Text aus dem bisher aktiven Editor übernehmen und neu rendern.
  useEffect(() => {
    const prev = prevBpRef.current;
    const prevIstBp = prev !== 'none' && briefpapier.some((b) => b.id === prev);
    const aktuellerInhalt = prevIstBp
      ? (briefEditorRef.current?.contentDocument?.getElementById('ce-inhalt')?.innerHTML ?? inhaltCacheRef.current)
      : (editorRef.current?.innerHTML ?? inhaltCacheRef.current);
    inhaltCacheRef.current = aktuellerInhalt;
    // Zitat-Zone ebenso aus dem bisher aktiven Editor übernehmen (überlebt den Wechsel).
    const aktuellesZitat = prevIstBp
      ? (briefEditorRef.current?.contentDocument?.getElementById('ce-zitat')?.innerHTML ?? zitatCacheRef.current)
      : (zitatEditorRef.current?.innerHTML ?? zitatCacheRef.current);
    zitatCacheRef.current = aktuellesZitat;
    if (briefpapierId !== 'none') {
      requestAnimationFrame(() => renderBriefEditor(inhaltCacheRef.current));
    } else if (prevIstBp) {
      requestAnimationFrame(() => {
        if (editorRef.current) editorRef.current.innerHTML = inhaltCacheRef.current;
        // Defense-in-depth (siehe Mount-Effekt) — auch beim Zurückwechseln aus dem Bogen sanitisieren.
        if (zitatEditorRef.current) zitatEditorRef.current.innerHTML = sanitizeHtml(zitatCacheRef.current);
      });
    }
    prevBpRef.current = briefpapierId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefpapierId, signaturId, briefpapier]);

  const updateToolbar = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !editorRef.current) { setToolbar(null); return; }
    const range = sel.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) { setToolbar(null); return; }
    const rect = range.getBoundingClientRect();
    const box = editorRef.current.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) { setToolbar(null); return; }
    setToolbar({
      top: rect.top - box.top - 46,
      left: Math.min(Math.max(rect.left - box.left + rect.width / 2, 90), box.width - 90),
    });
  }, []);

  const exec = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    updateToolbar();
  };
  const einfuegenLink = () => {
    const url = window.prompt('Link-URL (mit https://):', 'https://');
    if (url && url !== 'https://') exec('createLink', url);
  };

  // Formatierung im Briefpapier-Editor (wirkt auf das iframe-Dokument).
  const execBrief = (cmd: string, val?: string) => {
    const d = briefEditorRef.current?.contentDocument;
    if (!d) return;
    briefEditorRef.current?.contentWindow?.focus();
    d.execCommand(cmd, false, val);
    if (d.body) briefEditorRef.current!.style.height = `${d.body.scrollHeight + 8}px`;
    aktualisiereBriefFormat();
    setShowBriefFarbe(false);
    setShowBriefMarkierung(false);
    setShowBriefGroesse(false);
  };
  const briefLink = () => {
    const url = window.prompt('Link-URL (mit https://):', 'https://');
    if (url && url !== 'https://') execBrief('createLink', url);
  };

  // Pille: Escape schließt zuerst ein offenes Werkzeug-Popover, nicht das ganze Formular
  // (Capture-Phase, damit FormContent den Tastendruck als verbraucht sieht).
  const pillePopOffen = variant === 'pille' && (showVorlagen || showSignaturen || showBriefpapier || showAkten || showKi || showSpaeter);
  useEffect(() => {
    if (!pillePopOffen) return;
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setShowVorlagen(false); setShowSignaturen(false); setShowBriefpapier(false);
      setShowAkten(false); setShowKi(false); setShowSpaeter(false);
    };
    window.addEventListener('keydown', taste, true);
    return () => window.removeEventListener('keydown', taste, true);
  }, [pillePopOffen]);

  // ─── Theme (hell für Modal/Inline, dunkel für Pillen-Formular) ────────────
  const pille = variant === 'pille';
  const fenster = variant === 'fenster';
  // Dreifach-Wahl für Stellen, die hell/dunkel unterscheiden: Pille · Fenster (Tokens) · hell.
  const v3 = (p: string, f: string, h: string): string => (pille ? p : fenster ? f : h);
  const uiBasis = pille ? {
    fieldBorder: 'border-white/10',
    label: 'text-white/50',
    chip: 'bg-white/10 text-white/90',
    chipX: 'text-white/40 hover:text-white',
    fieldInput: 'text-white placeholder:text-white/30',
    acBox: 'bg-[#141416] border-white/10',
    acHover: 'hover:bg-white/5',
    acName: 'text-white',
    acMail: 'text-white/40',
    betreff: 'text-white placeholder:text-white/30 border-white/10 focus:border-white/25',
    editorText: 'text-white/90 [&_a]:text-[#c4a77d] [&:empty]:before:text-white/25',
    sectionLabel: 'text-white/40',
    atCard: 'border-white/10 bg-white/5',
    atName: 'text-white/90',
    atSize: 'text-white/40',
    atX: 'text-white/30 hover:text-red-400',
    footerBorder: 'border-white/10',
    toolBtn: 'text-white/50 hover:text-white hover:bg-white/10',
    toolActive: 'bg-white/10 text-white',
    vorlageBox: 'bg-[#1a1a1a] border-white/10',
    vorlageItem: 'text-white/80 hover:bg-white/5',
    sendBtn: 'bg-[#c4a77d] text-white hover:bg-[#b8956c]',
  } : fenster ? {
    fieldBorder: 'border-[var(--line)]',
    label: 'text-[var(--ink3)]',
    chip: 'bg-[var(--acc-soft)] text-[var(--acc)]',
    chipX: 'text-[var(--acc)] opacity-60 hover:opacity-100',
    fieldInput: 'text-[var(--ink)] placeholder:text-[var(--ink3)]',
    acBox: 'bg-[var(--bg)] border-[var(--line)]',
    acHover: 'hover:bg-[var(--hover)]',
    acName: 'text-[var(--ink)]',
    acMail: 'text-[var(--ink3)]',
    betreff: 'text-[var(--ink)] placeholder:text-[var(--ink3)] border-[var(--line)] focus:border-[var(--acc)]',
    editorText: 'text-gray-800',
    sectionLabel: 'text-[var(--ink3)]',
    atCard: 'border-transparent bg-[var(--fill2)]',
    atName: 'text-[var(--ink)]',
    atSize: 'text-[var(--ink3)]',
    atX: 'text-[var(--ink3)] hover:text-[var(--bad)]',
    footerBorder: 'border-[var(--line)]',
    toolBtn: 'text-[var(--ink2)] hover:text-[var(--ink)] hover:bg-[var(--hover)]',
    toolActive: 'bg-[var(--acc-soft)] text-[var(--acc)]',
    vorlageBox: 'bg-[var(--bg)] border-[var(--line)]',
    vorlageItem: 'text-[var(--ink)] hover:bg-[var(--hover)]',
    sendBtn: '[background:var(--acc-fill)] text-[var(--acc-ink)] [box-shadow:var(--acc-sh)] hover:opacity-90',
  } : {
    fieldBorder: 'border-gray-100',
    label: 'text-gray-400',
    chip: 'bg-gray-100 text-gray-700',
    chipX: 'text-gray-400 hover:text-gray-700',
    fieldInput: 'text-gray-900 placeholder:text-gray-300',
    acBox: 'bg-white border-gray-100',
    acHover: 'hover:bg-gray-50',
    acName: 'text-gray-900',
    acMail: 'text-gray-400',
    betreff: 'text-gray-900 placeholder:text-gray-300 border-gray-100 focus:border-[#1a1d24]/30',
    editorText: 'text-gray-800 [&_a]:text-[#1a1d24] [&:empty]:before:text-gray-300',
    sectionLabel: 'text-gray-400',
    atCard: 'border-gray-100 bg-white',
    atName: 'text-gray-800',
    atSize: 'text-gray-400',
    atX: 'text-gray-300 hover:text-red-500',
    footerBorder: 'border-gray-100',
    toolBtn: 'text-gray-400 hover:text-gray-900 hover:bg-gray-50',
    toolActive: 'bg-gray-100 text-gray-900',
    vorlageBox: 'bg-white border-gray-100',
    vorlageItem: 'text-gray-700 hover:bg-gray-50',
    sendBtn: 'bg-[#1a1d24] text-white hover:opacity-90',
  };
  // Schreibfenster mobil (gleiche Schwelle wie die MobileNav): Hit-Targets ≥ 44 px.
  const fensterMobil = fenster && (viewport.width < 900 || viewport.height < 500);
  const ziel44 = ' min-w-[44px] min-h-[44px] inline-flex items-center justify-center';
  const ui = fensterMobil
    ? { ...uiBasis, toolBtn: uiBasis.toolBtn + ziel44, toolActive: uiBasis.toolActive + ziel44, sendBtn: uiBasis.sendBtn + ' min-h-[44px]' }
    : uiBasis;

  const typBadge = (typ: Empfaenger['typ']): string => {
    if (fenster) {
      return typ === 'Mieter' ? 'bg-[var(--info-soft)] text-[var(--info)]'
        : typ === 'Eigentümer' ? 'bg-[var(--warn-soft)] text-[var(--warn)]'
        : typ === 'Dienstleister' ? 'bg-[var(--ok-soft)] text-[var(--ok)]'
        : typ === 'Kontakt' ? 'bg-[rgba(138,99,210,0.15)] text-[#8a63d2]'
        : 'bg-[var(--fill2)] text-[var(--ink2)]';
    }
    if (pille) {
      return typ === 'Mieter' ? 'bg-blue-500/20 text-blue-300'
        : typ === 'Eigentümer' ? 'bg-amber-500/20 text-amber-300'
        : typ === 'Dienstleister' ? 'bg-emerald-500/20 text-emerald-300'
        : typ === 'Kontakt' ? 'bg-violet-500/20 text-violet-300'
        : 'bg-white/10 text-white/50';
    }
    return typ === 'Mieter' ? 'bg-blue-50 text-blue-600'
      : typ === 'Eigentümer' ? 'bg-amber-50 text-amber-600'
      : typ === 'Dienstleister' ? 'bg-emerald-50 text-emerald-600'
      : typ === 'Kontakt' ? 'bg-violet-50 text-violet-600'
      : 'bg-gray-100 text-gray-500';
  };

  // ─── Recipient Field Renderer ─────────────────────────────────────────────

  const renderRecipientField = (
    label: string,
    field: 'an' | 'cc' | 'bcc',
    recipients: Empfaenger[],
    searchValue: string,
    rightSlot?: React.ReactNode,
  ) => {
    return (
      <div className="relative">
        <div className={`flex items-start gap-3 border-b pb-2 ${ui.fieldBorder}`}>
          <span className={`w-9 shrink-0 pt-2 text-[11px] font-black uppercase tracking-widest ${ui.label}`}>{label}</span>
          <div className="flex flex-1 flex-wrap items-center gap-1.5 min-w-0">
            {recipients.map(emp => (
              <span key={emp.email} className={`inline-flex items-center gap-1.5 rounded-full text-[12px] font-semibold ${fenster ? 'h-7 pl-2.5 pr-1.5 font-bold' : 'py-0.5 pl-0.5 pr-2'} ${ui.chip}`}>
                {!fenster && <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-black text-white ${avatarFarbe(emp.name || emp.email)}`}>{initialen(emp.name || emp.email)}</span>}
                <span className="truncate max-w-[180px]">{emp.name || emp.email}</span>
                <button type="button" onClick={() => removeEmpfaenger(emp.email, field)} aria-label={pille ? 'Empfänger entfernen' : undefined} data-tip={pille ? 'Empfänger entfernen' : undefined} className={`transition-colors ${ui.chipX}`}><X size={12} /></button>
              </span>
            ))}
            <input
              type="text"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value, field)}
              onKeyDown={(e) => handleKeyDown(e, field)}
              onPaste={(e) => handlePaste(e, field)}
              onFocus={() => setActiveField(field)}
              placeholder={recipients.length === 0 ? 'Name suchen oder E-Mail eingeben…' : ''}
              aria-label={pille ? label : undefined}
              className={`flex-1 min-w-[120px] bg-transparent py-1.5 text-sm font-semibold outline-none ${ui.fieldInput}`}
            />
          </div>
          {rightSlot && <div className="shrink-0 pt-1.5">{rightSlot}</div>}
        </div>

        {/* Autocomplete Dropdown — plus der freie Eintrag für Adressen ohne Kontakt */}
        {activeField === field && (searchResults.length > 0 || freieAdresse(searchValue)) && (
          <div className={`absolute z-50 left-0 right-0 mt-1 border rounded-xl shadow-xl overflow-hidden max-h-72 overflow-y-auto custom-scrollbar ${ui.acBox}`}>
            {freieAdresse(searchValue) && !searchResults.some((r) => r.email.toLowerCase() === adressenAusEingabe(searchValue)[0]?.toLowerCase()) && (
              <button
                type="button"
                onClick={() => uebernehmeAdressen(searchValue, field)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${ui.acHover}`}
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#c4a77d] text-white shrink-0"><Plus size={13} /></span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold truncate ${ui.acName}`}>An diese Adresse senden</p>
                  <p className={`text-[11px] truncate ${ui.acMail}`}>{adressenAusEingabe(searchValue).join(', ')}</p>
                </div>
              </button>
            )}
            {searchResults.map((result) => (
              <button
                key={`${result.typ}-${result.id}-${result.email}`}
                type="button"
                onClick={() => addEmpfaenger(result, field)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${ui.acHover}`}
              >
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-black shrink-0 ${fenster ? 'bg-[var(--fill2)] text-[var(--ink2)]' : 'text-white ' + avatarFarbe(result.name || result.email)}`}>{initialen(result.name || result.email)}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold truncate ${ui.acName}`}>{result.name || result.email}</p>
                  <p className={`text-[11px] truncate ${ui.acMail}`}>{result.email}{result.label ? ` · ${result.label}` : ''}</p>
                </div>
                <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${typBadge(result.typ)}`}>{result.typ}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ─── Geteilte, theme-fähige Bausteine (hell + dunkel) ─────────────────────

  const renderError = () => error && (
    <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-bold ${v3('bg-red-500/15 border border-red-500/30 text-red-300', 'bg-[var(--bad-soft)] text-[var(--bad)]', 'bg-red-50 border border-red-100 text-red-600')}`}>
      <X size={14} className="shrink-0" />
      {error}
      <button onClick={() => setError(null)} className={`ml-auto ${v3('text-red-300/70 hover:text-red-200', 'text-[var(--bad)]', 'text-red-400 hover:text-red-600')}`}><X size={14} /></button>
    </div>
  );

  const renderRecipients = () => (
    <>
      {renderRecipientField('An', 'an', an, anSearch, !showCcBcc ? (
        <button type="button" onClick={() => setShowCcBcc(true)} className={`text-[11px] font-black uppercase tracking-widest transition-colors ${v3('text-white/40 hover:text-[#c4a77d]', `rounded-[10px] px-2.5 py-1.5 bg-[var(--fill2)] text-[var(--ink)] normal-case tracking-normal font-bold ${fensterMobil ? 'min-h-[44px]' : ''}`, 'text-gray-400 hover:text-[#1a1d24]')}`}>Cc/Bcc</button>
      ) : null)}
      {showCcBcc && renderRecipientField('Cc', 'cc', cc, ccSearch)}
      {showCcBcc && renderRecipientField('Bcc', 'bcc', bcc, bccSearch)}
    </>
  );

  const renderBetreff = () => (
    <input
      type="text"
      value={betreff}
      onChange={(e) => setBetreff(e.target.value)}
      placeholder="Betreff"
      className={`w-full bg-transparent border-b pb-2 pt-1 text-lg font-bold outline-none transition-colors ${ui.betreff}`}
    />
  );

  // Einfacher Text-Editor (ohne Briefpapier): contentEditable + schwebende Format-Leiste.
  const renderPlainEditor = () => (
    <div className="relative pt-1">
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onMouseUp={updateToolbar}
        onKeyUp={updateToolbar}
        onInput={() => { inhaltCacheRef.current = editorRef.current?.innerHTML || ''; setBodyDirty((n) => n + 1); }}
        onBlur={() => setTimeout(() => setToolbar(null), 150)}
        data-placeholder="Ihre Nachricht…"
        className={`min-h-[200px] outline-none text-sm leading-relaxed text-gray-800 [&_a]:text-[#1a1d24] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-gray-300 [&:empty]:before:pointer-events-none ${pille || fenster ? 'rounded-xl bg-white px-4 py-3' : ''} ${fenster ? 'min-h-[260px] text-[15px]' : ''}`}
      />
      {/* Signatur-Vorschau (read-only) — ohne Briefpapier sonst unsichtbar; wird beim Senden unten angehängt. */}
      {signaturId !== 'none' && chosenSignaturHtml() && (
        <div className={pille || fenster ? 'mt-2 rounded-xl bg-white px-4 py-3' : 'mt-3 pt-3 border-t border-gray-200/70'}>
          <p className={`mb-1.5 text-[9px] font-black uppercase tracking-widest ${pille || fenster ? 'text-gray-400' : ui.sectionLabel}`}>Signatur</p>
          <div
            className="text-sm leading-relaxed text-gray-700 [&_a]:text-[#c4a77d] [&_a]:underline [&_img]:max-w-full [&_img]:h-auto"
            dangerouslySetInnerHTML={{ __html: chosenSignaturHtml() }}
          />
        </div>
      )}
      {/* Zitat-Karte — getrennt vom eigenen Text geführt, rendert NACH der Signatur.
          Bringt ihr Inline-Styling selbst mit (baueZitatKarte), daher kein eigener Rahmen. */}
      {zitatHtml !== null && (
        <div className="mt-2">
          <div
            ref={zitatEditorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={() => { zitatCacheRef.current = zitatEditorRef.current?.innerHTML || ''; setBodyDirty((n) => n + 1); }}
            className="outline-none text-sm leading-relaxed"
          />
        </div>
      )}
      {toolbar && (
        <div
          className="absolute z-30 flex items-center gap-0.5 rounded-lg bg-[#1a1d24] border border-white/10 px-1 py-1 shadow-xl -translate-x-1/2"
          style={{ top: toolbar.top, left: toolbar.left }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {[
            { icon: <Bold size={14} />, cmd: 'bold', t: 'Fett' },
            { icon: <Italic size={14} />, cmd: 'italic', t: 'Kursiv' },
            { icon: <Underline size={14} />, cmd: 'underline', t: 'Unterstrichen' },
            { icon: <Strikethrough size={14} />, cmd: 'strikeThrough', t: 'Durchgestrichen' },
          ].map(b => (
            <button key={b.cmd} type="button" title={b.t} onClick={() => exec(b.cmd)} className="rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors">{b.icon}</button>
          ))}
          <span className="mx-0.5 h-4 w-px bg-white/15" />
          {[
            { icon: <AlignLeft size={14} />, cmd: 'justifyLeft', t: 'Linksbündig' },
            { icon: <AlignCenter size={14} />, cmd: 'justifyCenter', t: 'Zentriert' },
            { icon: <AlignRight size={14} />, cmd: 'justifyRight', t: 'Rechtsbündig' },
          ].map(b => (
            <button key={b.cmd} type="button" title={b.t} onClick={() => exec(b.cmd)} className="rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors">{b.icon}</button>
          ))}
          <span className="mx-0.5 h-4 w-px bg-white/15" />
          <button type="button" title="Link einfügen" onClick={einfuegenLink} className="rounded p-1.5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"><Link2 size={14} /></button>
        </div>
      )}
    </div>
  );

  // Briefpapier-Editor: direkt im Bogen schreiben; Signatur erscheint unten im Bogen mit.
  const renderBriefpapierEditor = () => {
    const toolCls = (aktiv: boolean) => `p-1.5 rounded-lg transition-colors ${aktiv ? ui.toolActive : ui.toolBtn}`;
    const trenner = <span className={`mx-0.5 h-4 w-px ${v3('bg-white/15', 'bg-[var(--line)]', 'bg-gray-200')}`} />;
    const TEXT_FARBEN = ['#242424', '#C59D67', '#6b7280', '#b91c1c', '#15803d', '#1d4ed8'];
    const MARKIER_FARBEN = ['#fff3a3', '#bff5c3', '#bfe3ff', '#ffc9de', '#ffe0b3', '#e5e7eb'];
    const GROESSEN = [{ v: '2', l: 'Klein' }, { v: '3', l: 'Normal' }, { v: '4', l: 'Groß' }, { v: '5', l: 'Sehr groß' }];
    // Ein gemeinsames Farb-Menü für Textfarbe + Markierung (garantiert gleiches Aussehen).
    const farbMenu = (farben: string[], cmd: string, resetVal: string, resetLabel: string) => (
      <div className="absolute top-full left-0 mt-1 rounded-xl border border-gray-200 bg-white p-2.5 shadow-xl z-40">
        <div className="grid grid-cols-3 gap-2">
          {farben.map(c => (
            <button key={c} type="button" title={c} onMouseDown={(e) => e.preventDefault()} onClick={() => execBrief(cmd, c)} className="h-7 w-7 rounded-md ring-1 ring-inset ring-black/15 transition-transform hover:scale-110" style={{ backgroundColor: c }} />
          ))}
        </div>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execBrief(cmd, resetVal)} className="mt-2 w-full rounded-lg px-2 py-1 text-[11px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors">{resetLabel}</button>
      </div>
    );
    return (
    <div className="pt-1">
      {/* Umbruch statt horizontalem Scroll: die drei Ausklappmenüs (Schriftgröße/Textfarbe/
          Markierfarbe) sind `absolute top-full` innerhalb dieser Zeile positioniert. Ein
          overflow-x-auto-Container würde laut CSS-Spezifikation auch die Y-Achse auf `auto`
          zwingen und die Menüs vertikal abschneiden — und `.no-scrollbar` würde das noch
          unsichtbar machen. Nicht wieder auf Scroll umstellen, ohne die Menüs aus dem
          Overflow-Container herauszulösen (z. B. Portal). */}
      <div className="flex flex-wrap items-center gap-0.5 pb-2">
        {[
          { icon: <Bold size={15} />, cmd: 'bold', t: 'Fett' },
          { icon: <Italic size={15} />, cmd: 'italic', t: 'Kursiv' },
          { icon: <Underline size={15} />, cmd: 'underline', t: 'Unterstrichen' },
          { icon: <Strikethrough size={15} />, cmd: 'strikeThrough', t: 'Durchgestrichen' },
        ].map(b => (
          <button key={b.cmd} type="button" title={b.t} onMouseDown={(e) => e.preventDefault()} onClick={() => execBrief(b.cmd)} className={toolCls(!!briefFormat[b.cmd])}>{b.icon}</button>
        ))}
        {trenner}
        {/* Schriftgröße */}
        <div className="relative">
          <button type="button" title="Schriftgröße" onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowBriefGroesse(v => !v); setShowBriefFarbe(false); setShowBriefMarkierung(false); }} className={toolCls(showBriefGroesse)}><Type size={15} /></button>
          {showBriefGroesse && (
            <div className="absolute top-full left-0 mt-1 w-32 rounded-xl border border-gray-200 bg-white py-1 shadow-xl z-40">
              {GROESSEN.map(g => (
                <button key={g.v} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execBrief('fontSize', g.v)} className="w-full px-3 py-1.5 text-left text-[12px] font-semibold text-gray-700 hover:bg-gray-50 transition-colors">{g.l}</button>
              ))}
            </div>
          )}
        </div>
        {/* Textfarbe */}
        <div className="relative">
          <button type="button" title="Textfarbe" onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowBriefFarbe(v => !v); setShowBriefGroesse(false); setShowBriefMarkierung(false); }} className={toolCls(showBriefFarbe)}><Palette size={15} /></button>
          {showBriefFarbe && farbMenu(TEXT_FARBEN, 'foreColor', '#242424', 'Standard-Farbe')}
        </div>
        {/* Markierfarbe (Text-Hintergrund) */}
        <div className="relative">
          <button type="button" title="Markierfarbe" onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowBriefMarkierung(v => !v); setShowBriefFarbe(false); setShowBriefGroesse(false); }} className={toolCls(showBriefMarkierung)}><Highlighter size={15} /></button>
          {showBriefMarkierung && farbMenu(MARKIER_FARBEN, 'hiliteColor', 'transparent', 'Ohne Markierung')}
        </div>
        {trenner}
        <button type="button" title="Aufzählung" onMouseDown={(e) => e.preventDefault()} onClick={() => execBrief('insertUnorderedList')} className={toolCls(!!briefFormat['insertUnorderedList'])}><List size={15} /></button>
        <button type="button" title="Nummerierung" onMouseDown={(e) => e.preventDefault()} onClick={() => execBrief('insertOrderedList')} className={toolCls(!!briefFormat['insertOrderedList'])}><ListOrdered size={15} /></button>
        {trenner}
        {[
          { icon: <AlignLeft size={15} />, cmd: 'justifyLeft', t: 'Linksbündig' },
          { icon: <AlignCenter size={15} />, cmd: 'justifyCenter', t: 'Zentriert' },
          { icon: <AlignRight size={15} />, cmd: 'justifyRight', t: 'Rechtsbündig' },
        ].map(b => (
          <button key={b.cmd} type="button" title={b.t} onMouseDown={(e) => e.preventDefault()} onClick={() => execBrief(b.cmd)} className={toolCls(!!briefFormat[b.cmd])}>{b.icon}</button>
        ))}
        {trenner}
        <button type="button" title="Link einfügen" onMouseDown={(e) => e.preventDefault()} onClick={briefLink} className={toolCls(false)}><Link2 size={15} /></button>
        <button type="button" title="Formatierung entfernen" onMouseDown={(e) => e.preventDefault()} onClick={() => execBrief('removeFormat')} className={toolCls(false)}><RemoveFormatting size={15} /></button>
      </div>
      <div className={`rounded-xl overflow-hidden border ${v3('border-white/10', 'border-[var(--line)]', 'border-gray-200')}`}>
        <iframe ref={briefEditorRef} sandbox="allow-same-origin" title="Briefpapier" className="w-full bg-white border-0 block" style={{ minHeight: 320 }} />
      </div>
    </div>
    );
  };

  const renderEditor = () => (aktivesBriefpapier ? renderBriefpapierEditor() : renderPlainEditor());

  const anhangIconBg = (ext: string): string => {
    const img = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
    const doc = ['doc', 'docx'].includes(ext);
    const xls = ['xls', 'xlsx', 'csv'].includes(ext);
    if (fenster) {
      return ext === 'pdf' ? 'bg-[var(--bad-soft)] text-[var(--bad)]'
        : img || doc ? 'bg-[var(--info-soft)] text-[var(--info)]'
        : xls ? 'bg-[var(--ok-soft)] text-[var(--ok)]'
        : 'bg-[var(--bg)] text-[var(--ink3)]';
    }
    if (pille) {
      return ext === 'pdf' ? 'bg-red-500/20 text-red-300'
        : img ? 'bg-blue-500/20 text-blue-300'
        : doc ? 'bg-sky-500/20 text-sky-300'
        : xls ? 'bg-emerald-500/20 text-emerald-300'
        : 'bg-white/10 text-white/40';
    }
    return ext === 'pdf' ? 'bg-red-50 text-red-500'
      : img ? 'bg-blue-50 text-blue-500'
      : doc ? 'bg-sky-50 text-sky-600'
      : xls ? 'bg-emerald-50 text-emerald-600'
      : 'bg-gray-50 text-gray-400';
  };

  const renderAttachments = () => (
    <>
      {(anhaenge.length > 0 || autoAnhangLaedt) && (
        <div className="pt-1">
          <p className={`mb-2 text-[10px] font-black uppercase tracking-widest ${ui.sectionLabel}`}>{anhaenge.length + autoAnhangOffen} {anhaenge.length + autoAnhangOffen === 1 ? 'Anhang' : 'Anhänge'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {autoAnhangLaedt && (
              <div className={`flex items-center gap-2.5 rounded-xl border p-2.5 ${pille || fenster ? '' : 'shadow-sm'} ${ui.atCard}`}>
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${anhangIconBg('pdf')}`}><Loader2 size={17} className="animate-spin" /></div>
                <div className="flex-1 min-w-0">
                  <p className={`text-[12px] font-semibold truncate ${ui.atName}`}>{autoAnhang?.name || 'Anhang'}</p>
                  <p className={`text-[10px] font-medium uppercase ${ui.atSize}`}>lädt…</p>
                </div>
              </div>
            )}
            {anhaenge.map(anhang => {
              const ext = (anhang.name.split('.').pop() || '').toLowerCase();
              return (
                <div key={anhang.id} className={`flex items-center gap-2.5 rounded-xl border p-2.5 ${pille || fenster ? '' : 'shadow-sm'} ${ui.atCard}`}>
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${anhangIconBg(ext)}`}><FileText size={17} /></div>
                  <button type="button" onClick={() => void oeffneAnhang(anhang)} title="Anhang öffnen" className="flex-1 min-w-0 text-left cursor-pointer" style={{ background: 'transparent', border: 'none', padding: 0, fontFamily: 'inherit' }}>
                    <p className={`text-[12px] font-semibold truncate ${ui.atName}`}>{anhang.name}</p>
                    <p className={`text-[10px] font-medium uppercase ${ui.atSize}`}>{formatFileSize(anhang.size)}</p>
                  </button>
                  <button type="button" onClick={() => removeAnhang(anhang)} aria-label={pille ? 'Anhang entfernen' : undefined} data-tip={pille ? 'Anhang entfernen' : undefined} className={`p-1 transition-colors ${ui.atX}`}><X size={14} /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {dragOver && (
        <div className={`rounded-xl border-2 border-dashed py-6 text-center text-[11px] font-bold ${v3('border-white/25 bg-white/5 text-white/50', 'border-[var(--acc)] bg-[var(--acc-soft)] text-[var(--acc)]', 'border-[#1a1d24]/40 bg-gray-50 text-gray-400')}`}>Dateien hier ablegen…</div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => { if (e.target.files && e.target.files.length > 0) { uploadFiles(e.target.files); e.target.value = ''; } }}
      />
    </>
  );

  const renderFooter = () => (
    <div className={`shrink-0 border-t ${ui.footerBorder} px-4 py-3 flex items-center justify-between gap-2 ${fenster ? 'flex-wrap' : ''}`}>
      <div className={`flex items-center gap-1 ${fenster ? 'flex-wrap' : ''}`}>
        {/* Anhang hinzufügen */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          title="Anhang hinzufügen"
          className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${ui.toolBtn}`}
        >
          {uploading ? <Loader2 size={17} className="animate-spin" /> : <Paperclip size={17} />}
        </button>
        {/* Vorlage nutzen */}
        {vorlagen.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowVorlagen(v => !v)}
              title="Vorlage nutzen"
              className={`p-2 rounded-lg transition-colors ${showVorlagen ? ui.toolActive : ui.toolBtn}`}
            >
              <FileText size={17} />
            </button>
            {showVorlagen && (
              <div className={`absolute bottom-full left-0 mb-2 w-60 max-h-64 overflow-y-auto rounded-xl border py-1 shadow-xl z-40 custom-scrollbar ${ui.vorlageBox}`}>
                <p className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-widest ${ui.sectionLabel}`}>Vorlage nutzen</p>
                {vorlagen.map(v => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => { handleVorlageChange(v.id); setShowVorlagen(false); }}
                    className={`w-full px-4 py-2 text-left text-[12px] font-semibold transition-colors ${ui.vorlageItem}`}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Signatur wählen (erscheint unten im Briefbogen) */}
        {signaturen.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowSignaturen(v => !v)}
              title="Signatur"
              className={`p-2 rounded-lg transition-colors ${showSignaturen || signaturId !== 'none' ? ui.toolActive : ui.toolBtn}`}
            >
              <FileSignature size={17} />
            </button>
            {showSignaturen && (
              <div className={`absolute bottom-full left-0 mb-2 w-60 max-h-64 overflow-y-auto rounded-xl border py-1 shadow-xl z-40 custom-scrollbar ${ui.vorlageBox}`}>
                <p className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-widest ${ui.sectionLabel}`}>Signatur</p>
                <button type="button" onClick={() => { setSignaturId('none'); setShowSignaturen(false); }} className={`w-full px-4 py-2 text-left text-[12px] font-semibold transition-colors flex items-center justify-between gap-2 ${ui.vorlageItem}`}>
                  <span>Keine Signatur</span>{signaturId === 'none' && <span className="text-[#c4a77d] shrink-0">✓</span>}
                </button>
                {signaturen.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => { setSignaturId(s.id); setShowSignaturen(false); }}
                    className={`w-full px-4 py-2 text-left text-[12px] font-semibold transition-colors flex items-center justify-between gap-2 ${ui.vorlageItem}`}
                  >
                    <span className="truncate">{s.name}{s.ist_standard ? ' · Standard' : ''}</span>
                    {signaturId === s.id && <span className="text-[#c4a77d] shrink-0">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Briefpapier wählen */}
        {briefpapier.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowBriefpapier(v => !v)}
              title="Briefpapier"
              className={`p-2 rounded-lg transition-colors ${showBriefpapier || briefpapierId !== 'none' ? ui.toolActive : ui.toolBtn}`}
            >
              <Newspaper size={17} />
            </button>
            {showBriefpapier && (
              <div className={`absolute bottom-full left-0 mb-2 w-64 max-h-64 overflow-y-auto rounded-xl border py-1 shadow-xl z-40 custom-scrollbar ${ui.vorlageBox}`}>
                <p className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-widest ${ui.sectionLabel}`}>Briefpapier</p>
                <button type="button" onClick={() => { setBriefpapierId('none'); setShowBriefpapier(false); }} className={`w-full px-4 py-2 text-left text-[12px] font-semibold transition-colors flex items-center justify-between gap-2 ${ui.vorlageItem}`}>
                  <span>Kein Briefpapier</span>{briefpapierId === 'none' && <span className="text-[#c4a77d] shrink-0">✓</span>}
                </button>
                {briefpapier.map(b => (
                  <button key={b.id} type="button" onClick={() => { setBriefpapierId(b.id); setShowBriefpapier(false); }} className={`w-full px-4 py-2 text-left text-[12px] font-semibold transition-colors flex items-center justify-between gap-2 ${ui.vorlageItem}`}>
                    <span className="truncate">{b.name}{b.ist_standard ? ' · Standard' : ''}</span>{briefpapierId === b.id && <span className="text-[#c4a77d] shrink-0">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Aus Aktenschrank anhängen (Google Drive) — Ordner-Browser + Suche */}
        <div className="relative">
          <button
            type="button"
            onClick={oeffneAktenPicker}
            title="Aus Aktenschrank anhängen"
            className={`p-2 rounded-lg transition-colors ${showAkten ? ui.toolActive : ui.toolBtn}`}
          >
            <Archive size={17} />
          </button>
          {showAkten && (
            <div className={`absolute bottom-full left-0 mb-2 rounded-xl border py-1 shadow-xl z-40 ${ui.vorlageBox}`} style={{ width: 'min(560px, calc(100vw - 3rem))' /* #559: lange Dateinamen lesbar */ }}>
              <div className="px-3 pt-1.5 pb-2">
                <p className={`mb-1.5 text-[9px] font-black uppercase tracking-widest ${ui.sectionLabel}`}>Aus Aktenschrank anhängen</p>
                <div className="relative">
                  <Search size={14} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${ui.sectionLabel}`} />
                  <input
                    type="text"
                    value={aktenSuche}
                    onChange={(e) => handleAktenSuche(e.target.value)}
                    placeholder="Suchen oder Ordner durchklicken…"
                    className={`w-full rounded-lg border pl-8 pr-3 py-2 text-sm outline-none ${v3('bg-white/5 border-white/10 text-white placeholder:text-white/30', 'bg-[var(--fill2)] border-transparent text-[var(--ink)] placeholder:text-[var(--ink3)]', 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400')}`}
                  />
                </div>
              </div>

              {aktenSuche.trim().length >= 2 ? (
                /* ── Suche (Drive-weit) ── */
                <div className="max-h-64 overflow-y-auto custom-scrollbar px-1 pb-1">
                  {aktenLaedt ? (
                    <div className={`flex items-center gap-2 px-3 py-3 text-[12px] ${ui.sectionLabel}`}><Loader2 size={14} className="animate-spin" /> Suche…</div>
                  ) : aktenTreffer.length === 0 ? (
                    <p className={`px-3 py-3 text-[12px] ${ui.sectionLabel}`}>Keine Dokumente gefunden.</p>
                  ) : aktenTreffer.map((t) => {
                    const google = istGoogleFormat(t.mimeType);
                    const holt = aktenHolt === t.id;
                    const meta = [t.pfad, formatiereGroesse(t.size)].filter(Boolean).join(' · ') || (google ? 'Google-Dokument (nicht anhängbar)' : '');
                    return (
                      <button key={t.id} type="button" disabled={google || holt} onClick={() => haengeDateiAn(t.id, t.name, t.mimeType)}
                        className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${ui.vorlageItem}`}>
                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${v3('bg-white/10 text-white/70', 'bg-[var(--fill2)] text-[var(--ink2)]', 'bg-gray-100 text-gray-500')}`}>
                          {holt ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-semibold" title={t.name} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>{t.name}</p>
                          {meta && <p className={`truncate text-[10px] ${ui.atSize}`}>{meta}</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* ── Ordner-Browser (durchklicken) ── */
                <>
                  <div className={`flex items-center gap-0.5 flex-wrap px-3 pb-1.5 text-[11px] ${ui.sectionLabel}`}>
                    {aktenStack.map((s, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <ChevronRight size={12} className="opacity-60" />}
                        <button type="button" onClick={() => springeZuBreadcrumb(i)} className={`truncate max-w-[130px] hover:underline ${i === aktenStack.length - 1 ? 'font-bold ' + v3('text-white', 'text-[var(--ink)]', 'text-gray-900') : ''}`}>{s.name}</button>
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="max-h-56 overflow-y-auto custom-scrollbar px-1 pb-1">
                    {aktenBrowseLaedt ? (
                      <div className={`flex items-center gap-2 px-3 py-3 text-[12px] ${ui.sectionLabel}`}><Loader2 size={14} className="animate-spin" /> Lädt…</div>
                    ) : aktenInhalt.length === 0 ? (
                      <p className={`px-3 py-3 text-[12px] ${ui.sectionLabel}`}>Ordner ist leer.</p>
                    ) : aktenInhalt.map((e) => {
                      const istOrdner = e.mimeType === ORDNER_MIME;
                      const google = !istOrdner && istGoogleFormat(e.mimeType);
                      const holt = aktenHolt === e.id;
                      const groesse = !istOrdner ? formatiereGroesse(e.size) : '';
                      return (
                        <button key={e.id} type="button" disabled={google || holt}
                          onClick={() => istOrdner ? oeffneUnterordner(e) : haengeDateiAn(e.id, e.name, e.mimeType)}
                          className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${ui.vorlageItem}`}>
                          <div className={`flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${istOrdner ? v3('bg-[#c4a77d]/20 text-[#c4a77d]', 'bg-[var(--acc-soft)] text-[var(--acc)]', 'bg-amber-50 text-amber-500') : v3('bg-white/10 text-white/70', 'bg-[var(--fill2)] text-[var(--ink2)]', 'bg-gray-100 text-gray-500')}`}>
                            {holt ? <Loader2 size={15} className="animate-spin" /> : istOrdner ? <Folder size={15} /> : <FileText size={15} />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[12px] font-semibold" title={e.name} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>{e.name}</p>
                            {(groesse || google) && <p className={`truncate text-[10px] ${ui.atSize}`}>{google ? 'Google-Dokument (nicht anhängbar)' : groesse}</p>}
                          </div>
                          {istOrdner && <ChevronRight size={14} className={ui.sectionLabel} />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {/* KI: eigenen Text überarbeiten oder (im Antwort-Modus) eine Antwort entwerfen */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowKi((v) => !v)}
            title="KI"
            className={`p-2 rounded-lg transition-colors ${showKi ? ui.toolActive : ui.toolBtn}`}
          >
            <Sparkles size={17} />
          </button>
          {showKi && (
            <div className={`absolute bottom-full left-0 mb-2 ${kiRegelnOffen ? 'w-[440px]' : 'w-80'} rounded-xl border p-3 shadow-xl z-40 space-y-2.5 ${ui.vorlageBox}`}>
              <div className="flex items-center justify-between gap-2">
                <p className={`text-[9px] font-black uppercase tracking-widest ${ui.sectionLabel}`}>
                  {kiRegelnOffen ? 'KI · Regeln' : 'KI'}
                </p>
                {kiDarfRegeln && !kiRegelnOffen && kiVorschau === null && (
                  <button
                    type="button"
                    onClick={kiRegelnOeffnen}
                    title="Regeln der Überarbeitung bearbeiten (nur Admins)"
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest ${ui.sectionLabel} hover:opacity-70 transition-opacity`}
                  >
                    <Pencil size={11} /> Regeln
                  </button>
                )}
              </div>

              {kiRegelnOffen ? (
                <>
                  <p className={`text-[11px] leading-relaxed ${ui.sectionLabel}`}>
                    Diese Regeln bekommt die KI bei „Meinen Text überarbeiten". Sie gelten für alle im Team.
                  </p>
                  <textarea
                    value={kiRegeln}
                    onChange={(e) => setKiRegeln(e.target.value)}
                    rows={14}
                    className={`w-full resize-y rounded-lg border px-3 py-2 text-[12px] leading-relaxed outline-none ${v3('bg-white/5 border-white/10 text-white', 'bg-[var(--fill2)] border-transparent text-[var(--ink)]', 'bg-gray-50 border-gray-200 text-gray-900')}`}
                  />
                  {kiFehler && (
                    <p className={`text-[12px] font-semibold ${v3('text-red-300', 'text-[var(--bad)]', 'text-red-500')}`}>{kiFehler}</p>
                  )}
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={kiRegelnSpeichert}
                      onClick={kiRegelnSpeichern}
                      className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-[12px] font-bold disabled:opacity-50 ${ui.sendBtn}`}
                    >
                      {kiRegelnSpeichert ? <Loader2 size={14} className="animate-spin" /> : null}
                      Speichern
                    </button>
                    <button
                      type="button"
                      onClick={() => setKiRegeln(STANDARD_REGELN)}
                      title="Von Björn vorgegebenes Standard-Regelwerk wiederherstellen"
                      className={`rounded-lg border px-3 py-2 text-[12px] font-bold ${ui.vorlageItem} ${ui.fieldBorder}`}
                    >
                      Standard
                    </button>
                    <button
                      type="button"
                      onClick={() => { setKiRegelnOffen(false); setKiFehler(null); }}
                      className={`rounded-lg border px-3 py-2 text-[12px] font-bold ${ui.vorlageItem} ${ui.fieldBorder}`}
                    >
                      Abbrechen
                    </button>
                  </div>
                </>
              ) : kiVorschau === null ? (
                <>
                  <input
                    type="text"
                    value={kiAnweisung}
                    onChange={(e) => setKiAnweisung(e.target.value)}
                    placeholder="Anweisung (optional) …"
                    className={`w-full rounded-lg border px-3 py-2 text-sm outline-none ${v3('bg-white/5 border-white/10 text-white placeholder:text-white/30', 'bg-[var(--fill2)] border-transparent text-[var(--ink)] placeholder:text-[var(--ink3)]', 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400')}`}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {['Kürzer', 'Förmlicher', 'Freundlicher', 'Rechtschreibung korrigieren'].map((chip) => (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => setKiAnweisung(chip)}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${ui.chip} hover:opacity-80`}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                  {kiFehler && (
                    <p className={`text-[12px] font-semibold ${v3('text-red-300', 'text-[var(--bad)]', 'text-red-500')}`}>{kiFehler}</p>
                  )}
                  <div className="space-y-1.5 pt-0.5">
                    <button
                      type="button"
                      disabled={kiLaedt}
                      onClick={kiTextUeberarbeiten}
                      className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-[12px] font-bold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${ui.sendBtn}`}
                    >
                      {kiLaedt ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      Meinen Text überarbeiten
                    </button>
                    {mode === 'antwort' && (
                      <button
                        type="button"
                        disabled={kiLaedt}
                        onClick={kiAntwortEntwerfen}
                        className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-[12px] font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${ui.vorlageItem} border ${ui.fieldBorder}`}
                      >
                        {kiLaedt ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        Antwort entwerfen
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="max-h-64 overflow-y-auto custom-scrollbar rounded-lg border border-gray-200 bg-white p-2.5 text-[13px] leading-relaxed text-gray-800 whitespace-pre-wrap">
                    {kiVorschau}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={kiVorschauUebernehmen}
                      className={`flex-1 rounded-lg px-3 py-2 text-[12px] font-bold transition-opacity ${ui.sendBtn}`}
                    >
                      Übernehmen
                    </button>
                    <button
                      type="button"
                      onClick={kiVorschauVerwerfen}
                      className={`flex-1 rounded-lg px-3 py-2 text-[12px] font-bold transition-colors ${ui.vorlageItem} border ${ui.fieldBorder}`}
                    >
                      Verwerfen
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={`flex items-center gap-1.5 ${fenster ? 'ml-auto' : ''}`}>
        {/* Später senden */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSpaeter((v) => !v)}
            disabled={sending}
            title="Später senden"
            className={`p-2.5 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${showSpaeter ? ui.toolActive : ui.toolBtn}`}
          >
            <Clock size={17} />
          </button>
          {showSpaeter && (
            <div className={`absolute bottom-full right-0 mb-2 w-64 rounded-xl border p-2.5 shadow-xl z-40 space-y-1.5 ${ui.vorlageBox}`}>
              <p className={`px-1.5 py-1 text-[9px] font-black uppercase tracking-widest ${ui.sectionLabel}`}>Später senden</p>
              {spaeterSendenPresets(new Date()).map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => { setShowSpaeter(false); handleSpaeterSenden(p.datum.toISOString()); }}
                  className={`w-full px-3 py-2 rounded-lg text-left text-[12px] font-semibold transition-colors ${ui.vorlageItem}`}
                >
                  {p.label}
                </button>
              ))}
              <div className="flex items-center gap-1.5 pt-1">
                <input
                  type="datetime-local"
                  value={spaeterFrei}
                  onChange={(e) => setSpaeterFrei(e.target.value)}
                  className={`flex-1 px-2 py-1.5 rounded-lg border text-[12px] font-semibold outline-none ${fenster ? '' : '[color-scheme:light]'} ${v3('bg-white/5 border-white/10 text-white', 'bg-[var(--fill2)] border-transparent text-[var(--ink)]', 'bg-gray-50 border-gray-200 text-gray-900')}`}
                />
                <button
                  type="button"
                  disabled={!spaeterFrei || !lokalZuIso(spaeterFrei) || new Date(spaeterFrei).getTime() <= Date.now()}
                  onClick={() => {
                    const iso = lokalZuIso(spaeterFrei);
                    if (iso) { setShowSpaeter(false); handleSpaeterSenden(iso); }
                  }}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-opacity disabled:opacity-40 ${ui.sendBtn}`}
                >
                  OK
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Per Post (E-POST) */}
        <button
          type="button"
          onClick={() => void alsBrief()}
          disabled={sending}
          title="Per Post – diese Mail als Brief, Anhänge im selben Umschlag"
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-opacity disabled:opacity-50 ${v3('bg-white/10 text-white', 'bg-[var(--fill2)] text-[var(--ink)]', 'bg-gray-100 text-gray-900')}`}
        >
          <PhEnvelope size={16} weight="bold" /> Per Post
        </button>
        {/* Senden */}
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || anzahlEmpfaenger(an, cc, bcc) === 0}
          className={`inline-flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${ui.sendBtn}`}
        >
          {sending ? <><Loader2 size={16} className="animate-spin" /> Senden…</> : <><Send size={16} /> Senden</>}
        </button>
      </div>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  // Schreibfenster (E-Mail-Bereich): großes Fenster, mobil Vollbild. Klick daneben minimiert
  // (wie im Prototyp) – das Formular bleibt dabei gemountet, nichts geht verloren.
  if (fenster) {
    const telefon = viewport.width < 768;
    const unterLeiste = viewport.width < 900 || viewport.height < 500;
    const original = replyTo
      ? { id: replyTo.id, betreff: replyTo.betreff, von: replyTo.von, datum: replyTo.datum, html: replyTo.html_inhalt, text: replyTo.text_inhalt }
      : forward
        ? { id: forward.id, betreff: forward.betreff, von: null as string | null, datum: forward.datum, html: forward.html_inhalt, text: forward.text_inhalt }
        : null;
    const originalMoeglich = !!original && viewport.width >= 1100;
    const mitOriginal = originalMoeglich && originalAn;
    const vonZeile = konto.bezeichnung && konto.bezeichnung !== konto.email_adresse ? `${konto.bezeichnung} <${konto.email_adresse}>` : konto.email_adresse;
    const kopfKnopf = 'flex items-center justify-center shrink-0 rounded-[12px] text-[var(--ink2)] hover:bg-[var(--hover)] transition-colors';
    const datumText = (iso?: string) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
    return (
      <>
        {createPortal(
          <div className="fixed inset-0" style={{ zIndex: 95, pointerEvents: 'none', visibility: minimiert ? 'hidden' : 'visible', fontFamily: "'Manrope', -apple-system, sans-serif", color: 'var(--ink)' }}>
            {!telefon && (
              <div aria-hidden className="absolute" onMouseDown={() => setMinimiert(true)}
                style={{ top: 0, right: 0, bottom: 0, left: inhaltLinks, pointerEvents: 'auto', background: 'color-mix(in srgb, var(--bg) 55%, transparent)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }} />
            )}
            <div role="dialog" aria-modal="true" aria-label={modalTitle} data-ueber-modal={minimiert ? undefined : ''} className="immo-side-in absolute flex overflow-hidden"
              style={{
                pointerEvents: 'auto',
                // Prototyp: Fenster über der Inhaltsfläche rechts der Sidebar (links = Sidebar + 14, sonst 14 Rand);
                // Handy (< 768 px) im Vollbild unter der Insel-Pille.
                ...(telefon
                  ? { top: 'max(52px, env(safe-area-inset-top))', left: 0, right: 0, bottom: 0, borderRadius: '24px 24px 0 0' }
                  : { top: FENSTER_RAND, bottom: FENSTER_RAND, right: FENSTER_RAND, left: inhaltLinks + FENSTER_RAND, borderRadius: 32 }),
                background: 'var(--bg)', boxShadow: '0 40px 90px -30px rgba(0,0,0,0.55), inset 0 0 0 1px var(--line)',
              }}>
              <div className="flex flex-col flex-1 min-w-0">
                {/* Kopf */}
                <div className="flex items-center shrink-0" style={{ gap: 10, padding: telefon ? '12px 12px 6px' : '14px 16px 6px' }}>
                  <span className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: 'var(--acc-soft)' }}>
                    <PencilSimpleLine size={17} weight="fill" color="var(--acc)" />
                  </span>
                  <span className="flex-1 min-w-0 truncate" style={{ fontSize: 18, fontWeight: 800 }}>{modalTitle}</span>
                  {gespeichertUm && !telefon && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>Entwurf gespeichert · {gespeichertUm}</span>}
                  {originalMoeglich && (
                    <button type="button" onClick={() => setOriginalAn((v) => !v)} title="Original-E-Mail daneben anzeigen" aria-pressed={originalAn}
                      className="flex items-center shrink-0 rounded-[12px] transition-colors hover:bg-[var(--hover)]"
                      style={{ height: 36, padding: '0 12px', gap: 6, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', background: originalAn ? 'var(--acc-soft)' : 'var(--fill2)', color: originalAn ? 'var(--acc)' : 'var(--ink)' }}>
                      <Columns size={14} weight="bold" />Original
                    </button>
                  )}
                  <button type="button" onClick={() => setMinimiert(true)} title="Minimieren" aria-label="Minimieren" className={kopfKnopf} style={{ width: unterLeiste ? 44 : 38, height: unterLeiste ? 44 : 38 }}>
                    <Minus size={18} weight="bold" />
                  </button>
                  <button type="button" onClick={onClose} title="Schließen" aria-label="Schließen" className={kopfKnopf} style={{ width: unterLeiste ? 44 : 38, height: unterLeiste ? 44 : 38 }}>
                    <XPh size={18} weight="bold" />
                  </button>
                </div>
                {gespeichertUm && telefon && <span style={{ padding: '0 16px', fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>Entwurf gespeichert · {gespeichertUm}</span>}

                {/* Inhalt (scrollbar, Drag&Drop); unten weicher Auslauf zur Fußleiste statt harter Kante */}
                <div className="relative flex-1 min-h-0 flex flex-col">
                <div
                  className="flex-1 min-h-0 overflow-y-auto immo-scroll space-y-3"
                  style={{ padding: telefon ? '6px 12px 28px' : '6px 16px 30px' }}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  {renderError()}
                  <div className={`flex items-baseline gap-3 border-b pb-2 ${ui.fieldBorder}`} style={{ fontSize: 13 }}>
                    <span className={`w-9 shrink-0 text-[11px] font-black uppercase tracking-widest ${ui.label}`}>Von</span>
                    <span className="min-w-0 truncate" style={{ fontWeight: 600, color: 'var(--ink2)' }}>{vonZeile}</span>
                  </div>
                  {renderRecipients()}
                  {renderBetreff()}
                  {renderEditor()}
                  {renderAttachments()}
                </div>
                <div aria-hidden className="absolute pointer-events-none" style={{ left: 0, right: 0, bottom: 0, height: 24, background: 'linear-gradient(to bottom, color-mix(in srgb, var(--bg) 0%, transparent), var(--bg))' }} />
                </div>

                <div style={{ paddingBottom: unterLeiste && telefon ? 'env(safe-area-inset-bottom)' : 0 }}>{renderFooter()}</div>
              </div>

              {mitOriginal && original && (
                <aside aria-label="Original-E-Mail" className="immo-side-in immo-scroll flex flex-col shrink-0 overflow-y-auto"
                  style={{ width: 380, gap: 10, padding: 18, borderLeft: '1px solid var(--line)', background: 'var(--fill)' }}>
                  <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Original-E-Mail</span>
                  <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{original.betreff || '(Kein Betreff)'}</span>
                  <span style={{ fontSize: 13, color: 'var(--ink2)', overflowWrap: 'anywhere' }}>{[original.von, datumText(original.datum)].filter(Boolean).join(' · ')}</span>
                  <div style={{ paddingTop: 8, borderTop: '1px solid var(--line)' }}>
                    {original.html && original.id ? (
                      <EmailHtmlAnsicht html={original.html} emailId={original.id} />
                    ) : (
                      <span style={{ fontSize: 15, lineHeight: 1.65, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{original.text || ''}</span>
                    )}
                  </div>
                </aside>
              )}
            </div>
          </div>,
          document.body,
        )}
        {minimiert && createPortal(
          <div className="immo-glas immo-side-in fixed flex items-center"
            style={{ zIndex: 95, right: telefon ? 12 : 24, left: telefon ? 12 : 'auto', bottom: unterLeiste ? 96 : 24, gap: 10, padding: '6px 6px 6px 16px', borderRadius: 22, fontFamily: "'Manrope', -apple-system, sans-serif", color: 'var(--ink)' }}>
            <PencilSimpleLine size={16} weight="fill" color="var(--acc)" className="shrink-0" />
            <button type="button" onClick={() => setMinimiert(false)} className="flex-1 min-w-0 truncate text-left"
              style={{ maxWidth: telefon ? 'none' : 260, minHeight: 44, border: 'none', background: 'transparent', padding: 0, color: 'var(--ink)', fontSize: 13, fontWeight: 800 }}>
              Entwurf: {betreff.trim() || 'Ohne Betreff'}
            </button>
            <button type="button" onClick={() => setMinimiert(false)} title="Öffnen" aria-label="Öffnen" className="flex items-center justify-center shrink-0"
              style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'var(--acc-fill)', color: 'var(--acc-ink)' }}>
              <ArrowsOutSimple size={15} weight="bold" />
            </button>
          </div>,
          document.body,
        )}
      </>
    );
  }

  // Pillen-Variante (Handoff Pillen-Formulare §19): Abschnitte „Empfänger“ und „Nachricht“, Werkzeug-Dock
  // mit Popover über der Knopfleiste. Zustände und Handler sind dieselben wie in renderFooter().
  if (pille) {
    const schliessePops = () => {
      setShowVorlagen(false); setShowSignaturen(false); setShowBriefpapier(false);
      setShowAkten(false); setShowKi(false); setShowSpaeter(false);
    };
    const umschalten = (offen: boolean, setzen: (v: boolean) => void) => { schliessePops(); setzen(!offen); };
    const empfaengerZahl = anzahlEmpfaenger(an, cc, bcc);
    const feldInput = `w-full rounded-xl px-3 py-2 text-sm font-semibold outline-none placeholder:text-white/30`;
    const feldStil = { background: FELD.ruhe.flaeche, boxShadow: FELD.ruhe.ring, color: FORM.text };
    const haken = <PhCheck aria-hidden size={14} weight="bold" color={FORM.gold} className="shrink-0" />;
    const knopfGold = { background: KNOPF.gold.flaeche, color: KNOPF.gold.text };
    const knopfGrau = { background: KNOPF.grau.flaeche, color: KNOPF.grau.text };
    const kiFehlerText = kiFehler && <p className="text-[12px] font-semibold" style={{ color: FORM.rot }}>{kiFehler}</p>;

    let popover: React.ReactNode = null;
    if (showVorlagen) {
      popover = (
        <FormPopover titel="Vorlage nutzen" icon={PhFileText} verlauf={DOCK_VERLAUF.vorlage} onSchliessen={() => setShowVorlagen(false)}>
          <div className="pf-scroll flex flex-col overflow-y-auto" style={{ maxHeight: 248, gap: 2 }}>
            {vorlagen.map(v => (
              <FormPopoverZeile key={v.id} label={v.name} icon={PhFileText} onClick={() => { handleVorlageChange(v.id); setShowVorlagen(false); }} />
            ))}
          </div>
        </FormPopover>
      );
    } else if (showSignaturen) {
      popover = (
        <FormPopover titel="Signatur" icon={PhSignature} verlauf={DOCK_VERLAUF.signatur} breite={280} onSchliessen={() => setShowSignaturen(false)}>
          <div className="pf-scroll flex flex-col overflow-y-auto" style={{ maxHeight: 248, gap: 2 }}>
            <FormPopoverZeile label="Keine Signatur" an={signaturId === 'none'} rechts={signaturId === 'none' ? haken : undefined}
              onClick={() => { setSignaturId('none'); setShowSignaturen(false); }} />
            {signaturen.map(sg => (
              <FormPopoverZeile key={sg.id} label={`${sg.name}${sg.ist_standard ? ' · Standard' : ''}`} icon={PhSignature} an={signaturId === sg.id}
                rechts={signaturId === sg.id ? haken : undefined} onClick={() => { setSignaturId(sg.id); setShowSignaturen(false); }} />
            ))}
          </div>
        </FormPopover>
      );
    } else if (showBriefpapier) {
      popover = (
        <FormPopover titel="Briefpapier" icon={PhNewspaper} verlauf={DOCK_VERLAUF.briefpapier} breite={290} onSchliessen={() => setShowBriefpapier(false)}>
          <div className="pf-scroll flex flex-col overflow-y-auto" style={{ maxHeight: 248, gap: 2 }}>
            <FormPopoverZeile label="Kein Briefpapier" an={briefpapierId === 'none'} rechts={briefpapierId === 'none' ? haken : undefined}
              onClick={() => { setBriefpapierId('none'); setShowBriefpapier(false); }} />
            {briefpapier.map(b => (
              <FormPopoverZeile key={b.id} label={`${b.name}${b.ist_standard ? ' · Standard' : ''}`} icon={PhNewspaper} an={briefpapierId === b.id}
                rechts={briefpapierId === b.id ? haken : undefined} onClick={() => { setBriefpapierId(b.id); setShowBriefpapier(false); }} />
            ))}
          </div>
        </FormPopover>
      );
    } else if (showAkten) {
      popover = (
        <FormPopover titel="Aus Aktenschrank anhängen" icon={PhArchive} verlauf={DOCK_VERLAUF.akten} breite={560} onSchliessen={() => setShowAkten(false)}>
          <label className="flex items-center" style={{ gap: 8, padding: '8px 12px', borderRadius: 13, background: POPOVER.innen }}>
            <PhLupe aria-hidden size={14} weight="bold" color="rgba(255,255,255,0.4)" />
            <input type="text" value={aktenSuche} onChange={(e) => handleAktenSuche(e.target.value)} placeholder="Suchen oder Ordner durchklicken…"
              aria-label="Suchen oder Ordner durchklicken…" className="pf-klein min-w-0 flex-1 border-none bg-transparent outline-none placeholder:text-white/30"
              style={{ font: 'inherit', fontSize: 13.5, fontWeight: 600, color: FORM.text, boxShadow: 'none', borderRadius: 6 }} />
          </label>
          {aktenSuche.trim().length >= 2 ? (
            /* ── Suche (Drive-weit) ── */
            <div className="pf-scroll flex flex-col overflow-y-auto" style={{ maxHeight: 248, gap: 2 }}>
              {aktenLaedt ? (
                <div className="flex items-center" style={{ gap: 8, padding: 12, fontSize: 12, color: FORM.text3 }}><Loader2 size={14} className="animate-spin" /> Suche…</div>
              ) : aktenTreffer.length === 0 ? (
                <p style={{ margin: 0, padding: 12, fontSize: 12, color: FORM.text3 }}>Keine Dokumente gefunden.</p>
              ) : aktenTreffer.map((t) => {
                const google = istGoogleFormat(t.mimeType);
                const holt = aktenHolt === t.id;
                const meta = [t.pfad, formatiereGroesse(t.size)].filter(Boolean).join(' · ') || (google ? 'Google-Dokument (nicht anhängbar)' : '');
                return (
                  <FormPopoverZeile zweizeilig key={t.id} label={t.name} unter={meta || undefined} icon={PhFileText} gesperrt={google || holt}
                    rechts={holt ? <Loader2 size={14} className="animate-spin shrink-0" /> : undefined}
                    onClick={() => haengeDateiAn(t.id, t.name, t.mimeType)} />
                );
              })}
            </div>
          ) : (
            /* ── Ordner-Browser (durchklicken) ── */
            <>
              <div className="flex flex-wrap items-center" style={{ gap: 2, padding: '0 4px', fontSize: 11.5, fontWeight: 700, color: FORM.text3 }}>
                {aktenStack.map((st, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <PhCaretRight size={10} weight="bold" />}
                    <button type="button" onClick={() => springeZuBreadcrumb(i)} className="max-w-[130px] cursor-pointer truncate border-none bg-transparent p-0 hover:underline"
                      style={{ font: 'inherit', color: i === aktenStack.length - 1 ? FORM.text : FORM.gold }}>{st.name}</button>
                  </React.Fragment>
                ))}
              </div>
              <div className="pf-scroll flex flex-col overflow-y-auto" style={{ maxHeight: 224, gap: 2 }}>
                {aktenBrowseLaedt ? (
                  <div className="flex items-center" style={{ gap: 8, padding: 12, fontSize: 12, color: FORM.text3 }}><Loader2 size={14} className="animate-spin" /> Lädt…</div>
                ) : aktenInhalt.length === 0 ? (
                  <p style={{ margin: 0, padding: 12, fontSize: 12, color: FORM.text3 }}>Ordner ist leer.</p>
                ) : aktenInhalt.map((e) => {
                  const istOrdner = e.mimeType === ORDNER_MIME;
                  const google = !istOrdner && istGoogleFormat(e.mimeType);
                  const holt = aktenHolt === e.id;
                  const groesse = !istOrdner ? formatiereGroesse(e.size) : '';
                  return (
                    <FormPopoverZeile zweizeilig key={e.id} label={e.name} unter={google ? 'Google-Dokument (nicht anhängbar)' : groesse || undefined}
                      icon={istOrdner ? PhFolder : PhFileText}
                      iconFarbe={istOrdner ? ZEILEN_ICON.gold.farbe : undefined} iconFlaeche={istOrdner ? ZEILEN_ICON.gold.flaeche : undefined}
                      gesperrt={google || holt}
                      rechts={holt ? <Loader2 size={14} className="animate-spin shrink-0" /> : istOrdner ? <PhCaretRight size={14} weight="bold" color={FORM.text3} /> : undefined}
                      onClick={() => (istOrdner ? oeffneUnterordner(e) : haengeDateiAn(e.id, e.name, e.mimeType))} />
                  );
                })}
              </div>
            </>
          )}
        </FormPopover>
      );
    } else if (showKi) {
      popover = (
        <FormPopover titel={kiRegelnOffen ? 'KI · Regeln' : 'KI'} icon={PhSparkle} verlauf={DOCK_VERLAUF.ki} breite={kiRegelnOffen ? 440 : 330}
          onSchliessen={() => setShowKi(false)}
          rechts={kiDarfRegeln && !kiRegelnOffen && kiVorschau === null ? (
            <button type="button" onClick={kiRegelnOeffnen} data-tip="Regeln der Überarbeitung bearbeiten (nur Admins)"
              className="flex shrink-0 cursor-pointer items-center rounded-full border-none"
              style={{ gap: 4, height: 24, padding: '0 9px', font: 'inherit', fontSize: 11, fontWeight: 800, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)' }}>
              <PhPencil size={11} weight="bold" /> Regeln
            </button>
          ) : undefined}>
          {kiRegelnOffen ? (
            <>
              <p style={{ margin: 0, padding: '0 4px', fontSize: 11.5, fontWeight: 600, lineHeight: 1.5, color: FORM.text2 }}>
                Diese Regeln bekommt die KI bei „Meinen Text überarbeiten". Sie gelten für alle im Team.
              </p>
              <textarea value={kiRegeln} onChange={(e) => setKiRegeln(e.target.value)} rows={14}
                className={`${feldInput} resize-y leading-relaxed`} style={{ ...feldStil, fontSize: 12 }} />
              {kiFehlerText}
              <div className="flex items-center" style={{ gap: 6 }}>
                <button type="button" disabled={kiRegelnSpeichert} onClick={kiRegelnSpeichern}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-[12px] font-extrabold disabled:opacity-50" style={knopfGold}>
                  {kiRegelnSpeichert ? <Loader2 size={14} className="animate-spin" /> : null}
                  Speichern
                </button>
                <button type="button" onClick={() => setKiRegeln(STANDARD_REGELN)} data-tip="Von Björn vorgegebenes Standard-Regelwerk wiederherstellen"
                  className="rounded-xl px-3 py-2 text-[12px] font-extrabold" style={knopfGrau}>
                  Standard
                </button>
                <button type="button" onClick={() => { setKiRegelnOffen(false); setKiFehler(null); }}
                  className="rounded-xl px-3 py-2 text-[12px] font-extrabold" style={knopfGrau}>
                  Abbrechen
                </button>
              </div>
            </>
          ) : kiVorschau === null ? (
            <>
              <label className="flex items-center" style={{ gap: 8, padding: '8px 12px', borderRadius: 13, background: POPOVER.innen }}>
                <PhSparkle aria-hidden size={14} weight="bold" color="rgba(255,255,255,0.4)" />
                <input type="text" value={kiAnweisung} onChange={(e) => setKiAnweisung(e.target.value)} placeholder="Anweisung (optional) …"
                  aria-label="Anweisung (optional) …" className="pf-klein min-w-0 flex-1 border-none bg-transparent outline-none placeholder:text-white/30"
                  style={{ font: 'inherit', fontSize: 13.5, fontWeight: 600, color: FORM.text, boxShadow: 'none', borderRadius: 6 }} />
              </label>
              <div className="flex flex-wrap" style={{ gap: 6 }}>
                {['Kürzer', 'Förmlicher', 'Freundlicher', 'Rechtschreibung korrigieren'].map((chip) => (
                  <button key={chip} type="button" onClick={() => setKiAnweisung(chip)}
                    className="cursor-pointer rounded-full border-none px-2.5 py-1 text-[11.5px] font-bold"
                    style={{ background: kiAnweisung === chip ? 'rgba(226,192,141,0.2)' : 'rgba(255,255,255,0.08)', color: kiAnweisung === chip ? FORM.gold : 'rgba(255,255,255,0.78)' }}>
                    {chip}
                  </button>
                ))}
              </div>
              {kiFehlerText}
              <button type="button" disabled={kiLaedt} onClick={kiTextUeberarbeiten}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-[12px] font-extrabold disabled:cursor-not-allowed disabled:opacity-50" style={knopfGold}>
                {kiLaedt ? <Loader2 size={14} className="animate-spin" /> : <PhSparkle size={14} weight="bold" />}
                Meinen Text überarbeiten
              </button>
              {mode === 'antwort' && (
                <button type="button" disabled={kiLaedt} onClick={kiAntwortEntwerfen}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-[12px] font-extrabold disabled:cursor-not-allowed disabled:opacity-50" style={knopfGrau}>
                  {kiLaedt ? <Loader2 size={14} className="animate-spin" /> : <PhSparkle size={14} weight="bold" />}
                  Antwort entwerfen
                </button>
              )}
            </>
          ) : (
            <>
              <div className="pf-scroll overflow-y-auto whitespace-pre-wrap" style={{ maxHeight: 220, padding: '11px 12px', borderRadius: 14, background: '#fff', fontSize: 13, lineHeight: 1.55, color: '#242424' }}>
                {kiVorschau}
              </div>
              <div className="flex items-center" style={{ gap: 6 }}>
                <button type="button" onClick={kiVorschauUebernehmen} className="flex-1 rounded-xl px-3 py-2 text-[12px] font-extrabold" style={knopfGold}>Übernehmen</button>
                <button type="button" onClick={kiVorschauVerwerfen} className="flex-1 rounded-xl px-3 py-2 text-[12px] font-extrabold" style={knopfGrau}>Verwerfen</button>
              </div>
            </>
          )}
        </FormPopover>
      );
    } else if (showSpaeter) {
      popover = (
        <FormPopover titel="Später senden" icon={PhClock} verlauf={DOCK_VERLAUF.spaeter} breite={290} ausrichtung="rechts" onSchliessen={() => setShowSpaeter(false)}>
          <div className="flex flex-col" style={{ gap: 2 }}>
            {spaeterSendenPresets(new Date()).map((pr) => (
              <FormPopoverZeile key={pr.label} label={pr.label} icon={PhClock} onClick={() => { setShowSpaeter(false); handleSpaeterSenden(pr.datum.toISOString()); }} />
            ))}
          </div>
          <div className="flex items-center" style={{ gap: 6, padding: '4px 2px 0' }}>
            <input type="datetime-local" value={spaeterFrei} onChange={(e) => setSpaeterFrei(e.target.value)} aria-label="Später senden"
              className={`${feldInput} min-w-0 flex-1`} style={{ ...feldStil, fontSize: 12.5, colorScheme: 'dark' }} />
            <button type="button"
              disabled={!spaeterFrei || !lokalZuIso(spaeterFrei) || new Date(spaeterFrei).getTime() <= Date.now()}
              onClick={() => {
                const iso = lokalZuIso(spaeterFrei);
                if (iso) { setShowSpaeter(false); handleSpaeterSenden(iso); }
              }}
              className="rounded-xl px-3 py-2 text-[12px] font-extrabold disabled:opacity-40" style={knopfGold}>
              OK
            </button>
          </div>
        </FormPopover>
      );
    }

    return (
      <FormRahmen fehler={error} onFehlerSchliessen={() => setError(null)} knopfleiste={
        <FormKnopfleiste
          ueber={popover}
          links={
            <FormDock beschriftung="Werkzeuge" schmal={viewport.width < 600} eintraege={[
              { key: 'anhang', label: 'Anhang hinzufügen', icon: PhPaperclip, verlauf: DOCK_VERLAUF.anhang, laedt: uploading,
                onClick: () => { schliessePops(); fileInputRef.current?.click(); } },
              ...(vorlagen.length > 0 ? [{ key: 'vorlage', label: 'Vorlage nutzen', icon: PhFileText, verlauf: DOCK_VERLAUF.vorlage, offen: showVorlagen,
                onClick: () => umschalten(showVorlagen, setShowVorlagen) }] : []),
              ...(signaturen.length > 0 ? [{ key: 'signatur', label: 'Signatur', icon: PhSignature, verlauf: DOCK_VERLAUF.signatur, offen: showSignaturen,
                punkt: signaturId !== 'none', onClick: () => umschalten(showSignaturen, setShowSignaturen) }] : []),
              ...(briefpapier.length > 0 ? [{ key: 'briefpapier', label: 'Briefpapier', icon: PhNewspaper, verlauf: DOCK_VERLAUF.briefpapier, offen: showBriefpapier,
                punkt: briefpapierId !== 'none', onClick: () => umschalten(showBriefpapier, setShowBriefpapier) }] : []),
              { key: 'akten', label: 'Aus Aktenschrank anhängen', icon: PhArchive, verlauf: DOCK_VERLAUF.akten, offen: showAkten,
                onClick: () => { const offen = showAkten; schliessePops(); if (!offen) oeffneAktenPicker(); } },
              { key: 'ki', label: 'KI', icon: PhSparkle, verlauf: DOCK_VERLAUF.ki, offen: showKi, onClick: () => umschalten(showKi, setShowKi) },
            ]} />
          }
          zusatz={
            <>
            <button type="button" onClick={() => { schliessePops(); void alsBrief(); }} disabled={sending}
              aria-label="Per Post" data-tip="Per Post – diese Mail als Brief, Anhänge im selben Umschlag"
              className="pf-knopf flex shrink-0 cursor-pointer items-center justify-center border-none disabled:cursor-not-allowed disabled:opacity-50"
              style={{ width: KNOPF.hoehe, height: KNOPF.hoehe, borderRadius: KNOPF.radius, background: KNOPF.grau.flaeche, color: FORM.text }}>
              <PhEnvelope size={18} weight="bold" />
            </button>
            <button type="button" onClick={() => umschalten(showSpaeter, setShowSpaeter)} disabled={sending}
              aria-label="Später senden" aria-expanded={showSpaeter} data-tip="Später senden"
              className="pf-knopf flex shrink-0 cursor-pointer items-center justify-center border-none disabled:cursor-not-allowed disabled:opacity-50"
              style={{ width: KNOPF.hoehe, height: KNOPF.hoehe, borderRadius: KNOPF.radius, background: showSpaeter ? 'rgba(255,255,255,0.16)' : KNOPF.grau.flaeche, color: FORM.text }}>
              <PhClock size={18} weight="bold" />
            </button>
            </>
          }
          haupt={{
            label: 'Senden', ladeLabel: 'Senden…', icon: PaperPlaneTilt, typ: 'button', onClick: handleSend, laedt: sending,
            nurIcon: viewport.width < 600,
            gesperrt: !sending && empfaengerZahl === 0, gesperrtGrund: 'Mindestens einen Empfänger angeben',
          }} />
      }>
        <FormAbschnitt titel="Empfänger" icon={PhUsers} farbe="blau" index={0}
          rechts={!showCcBcc ? (
            <button type="button" onClick={() => setShowCcBcc(true)}
              className="flex shrink-0 cursor-pointer items-center rounded-full border-none"
              style={{ gap: 4, height: 28, padding: '0 11px', font: 'inherit', fontSize: 11.5, fontWeight: 800, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)' }}>
              <PhPlus size={11} weight="bold" /> Cc/Bcc
            </button>
          ) : undefined}>
          {/* pf-feld: Fokus ohne zweiten Ring am Eingabefeld (wie in der Feldhülle). */}
          <div className="pf-feld flex flex-col" style={{ gap: 6, padding: '6px 12px', borderRadius: FELD.radius, background: FELD.ruhe.flaeche, boxShadow: FELD.ruhe.ring }}>
            {renderRecipientField('An', 'an', an, anSearch)}
            {showCcBcc && renderRecipientField('Cc', 'cc', cc, ccSearch)}
            {showCcBcc && renderRecipientField('Bcc', 'bcc', bcc, bccSearch)}
          </div>
          <FormFeld label="Betreff" value={betreff} onChange={(e) => setBetreff(e.target.value)} placeholder="Betreff" />
        </FormAbschnitt>
        <FormAbschnitt titel="Nachricht" icon={PhTextAlignLeft} farbe="gold" index={1}>
          {renderEditor()}
          {renderAttachments()}
        </FormAbschnitt>
      {perPost && (
        <PostVersandDialog anlagen={perPost.anlagen}
          start={{ treffer: perPost.treffer, betreff: perPost.betreff, text: perPost.text, mitAnschreiben: true }}
          onSchliessen={() => setPerPost(null)} onGesendet={(m) => { setPerPost(null); setPostMeldung(m); }} />
      )}
      {postMeldung && (
        <p role="status" style={{ margin: '8px 0 0', fontSize: 12.5, fontWeight: 600, color: 'var(--ok, #2a8f5c)' }}>✓ {postMeldung}</p>
      )}
      </FormRahmen>
    );
  }

  const inline = variant === 'inline';
  // Schmal ODER kurz: das 700-px-Modal hat keinen Platz. Dieselbe Entscheidung
  // wie bei der Pille (Etappe E1.5) — bewusst KEINE eigene Abfrage, die App hat
  // schon zu viele konkurrierende "ist mobil"-Definitionen.
  const vollbild = !inline && !pille && istKompakt(viewport.width, viewport.height);

  return (
    <div
      className={inline
        ? 'flex h-full w-full'
        : vollbild
          ? 'fixed inset-0 z-[300] bg-[#f7f7f8]'
          : 'fixed inset-0 z-[300] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4'}
      onClick={inline || vollbild ? undefined : onClose}
    >
      <div
        className={inline
          ? 'bg-white rounded-2xl border border-gray-100 shadow-sm w-full flex flex-col overflow-hidden'
          : vollbild
            ? 'bg-white w-full h-full flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]'
            : 'bg-white rounded-[2rem] shadow-2xl w-full max-w-[700px] max-h-[90vh] flex flex-col'}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header (hell) ── */}
        <div className="flex items-center justify-between px-6 py-4 kurz:px-4 kurz:py-2 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900">{modalTitle}</h2>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 truncate">{konto.email_adresse}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-300 hover:text-gray-600 hover:bg-gray-50 transition-colors"><X size={18} /></button>
        </div>

        {/* ── Body (scrollbar, Drag&Drop) ── */}
        <div
          className="flex-1 overflow-y-auto px-6 py-4 space-y-3"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {renderError()}
          {renderRecipients()}
          {renderBetreff()}
          {renderEditor()}
          {renderAttachments()}
        </div>

        {renderFooter()}
        <>{perPost && (
          <PostVersandDialog anlagen={perPost.anlagen}
            start={{ treffer: perPost.treffer, betreff: perPost.betreff, text: perPost.text, mitAnschreiben: true }}
            onSchliessen={() => setPerPost(null)} onGesendet={(m) => { setPerPost(null); setPostMeldung(m); }} />
        )}
        {postMeldung && (
          <p role="status" style={{ margin: '8px 0 0', fontSize: 12.5, fontWeight: 600, color: 'var(--ok, #2a8f5c)' }}>✓ {postMeldung}</p>
        )}</>
      </div>
    </div>
  );
};

export default EmailVerfassen;
